import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AcidSolubilityOutputDto, ApiSedimentationOutputDto, BatchTestBriefDto, BasicCrushOutputDto, CompressiveStrengthOutputDto, CompressiveStrengthOutputReadingDto, ExpansionOutputDto, FluidLossOutputDto, FoamStabilityOutputDto, FreeFluidOutputDto, HpHtRheologyDirectionalReadingDto, HpHtRheologyOutputDto, HpHtRheologyOutputReadingDto, PressurizedMudScaleOutputDto, PycnometerOutputDto, RheologyOutputDto, RheologyOutputReadingDto, ShearBondOutputDto, SlurryBatchDto, SlurryService, SplittingTsOutputDto, StaticGelOutputDto, TriaxialConfinedCsOutputDto, TriaxialConfinedCsOutputReadingDto, UnconfinedCsOutputDto, UnconfinedCsOutputReadingDto } from '../../../../services/slurry.service';
import { UserSearchDialogComponent, UserSearchDialogResult } from '../../../../shared/user-search-dialog/user-search-dialog.component';
import { sumFiniteNumbers } from '../../utils/slurry-material-calculation.helpers';

const RAW_FILE_FIELD_KEYS = new Set([
  'equipmentno', 'equipmentnumber', 'equipment',
  'startdate', 'starteddate', 'starttime', 'startedtime',
  'enddate', 'stopdate', 'stoppeddate', 'endtime', 'stoptime', 'stoppedtime',
  'initialbc', 'initialbeardenconsistency', 'bc70', '70bc',
  'vol5min', 'volume5min', 'filtratevol5min', 'filtratevolume5min', '5minute', '5min',
  'vol15min', 'volume15min', 'filtratevol15min', 'filtratevolume15min', '15minute', '15min',
  'vol30min', 'volume30min', 'filtratevol30min', 'filtratevolume30min', '30minute', '30min',
  'timeto100psi', 'psi100', '100psi', 'timeto500psi', 'psi500', '500psi',
  'pressure12hr', 'hr12psi', '12hrpsi', '12hr',
  'pressure24hr', 'day1psi', '24hrpsi', '24hr',
  'pressure48hr', 'day2psi', '48hrpsi', '48hr',
  'endpressure', 'endpsi', 'totalhours', 'totaltime', 'endtimehrs', 'endtimehours'
]);

export type GenericTestOutputKind = 'thickening' | 'fluidLoss' | 'freeFluid' | 'rheology' | 'hphtRheology' | 'staticGelSgsa' | 'staticGelMacs' | 'compressiveStrengthUca' | 'compressiveStrengthCrush' | 'foamStability' | 'acidSolubility' | 'basicCrush' | 'apiSedimentation' | 'expansion' | 'pressurizedMudScale' | 'pycnometer' | 'shearBond' | 'splittingTs' | 'triaxialConfinedCs' | 'unconfinedCs' | 'other';

export interface TestOutputScheduleDefaults {
  finalTemperature: number | null;
  finalPressure: number | null;
  temperatureRampTime: number | null;
}

export interface ThickeningOutputDialogData {
  mode: 'review' | 'record';
  output?: BatchTestBriefDto | null;
  slurryId: number;
  testInputMasterId: number | null;
  batches: SlurryBatchDto[];
  kind?: GenericTestOutputKind;
  title?: string;
  staticGelEquipmentTypeId?: number | null;
  compressiveStrengthTypeId?: number | null;
  crushTestTypeId?: number | null;
  foamedStabilityTypeId?: number | null;
  rheologyIsSurface?: boolean;
  scheduleDefaults?: TestOutputScheduleDefaults | null;
  testInputTemperature?: number | null;
  testInputPressure?: number | null;
  slurryStatusId?: number | null;
  isParentProjectCompleted?: boolean | null;
  workflowActions?: Partial<TestOutputWorkflowActions>;
}

export interface TestOutputWorkflowActions {
  canRecord: boolean;
  canFail: boolean;
  canCancel: boolean;
  canRevert: boolean;
  canUpdate: boolean;
  canVerify: boolean;
  canRetake: boolean;
}

@Component({
  selector: 'app-legacy-cai-search',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './legacy-cai-search.component.html',
  styleUrls: ['./legacy-cai-search.component.css']
})
export class LegacyCaiSearchComponent {
  @Input() value = '';
  @Output() valueChange = new EventEmitter<string>();
  @Input() readonly = false;
  @Input() fieldLabel = 'User';

  constructor(private readonly dialog: MatDialog) {}

  clear(): void {
    if (this.readonly) {
      return;
    }
    this.valueChange.emit('');
  }

  openSearch(): void {
    if (this.readonly) {
      return;
    }

    const ref = this.dialog.open(UserSearchDialogComponent, {
      data: { fieldLabel: this.fieldLabel },
      width: '1400px',
      maxWidth: '95vw',
      disableClose: false
    });
    ref.afterClosed().subscribe((result: UserSearchDialogResult | undefined) => {
      if (result) {
        this.valueChange.emit(result.email);
      }
    });
  }
}

@Component({
  selector: 'app-thickening-output-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, LegacyCaiSearchComponent],
  templateUrl: './thickening-output-dialog.component.html',
  styleUrls: ['./thickening-output-dialog.component.css']
})
export class ThickeningOutputDialogComponent {
  readonly confinedReadingLabels = ['Tangent', 'Average', 'Secant'];
  ctTemp: number | null = null;
  thickeningPressure: number | null = null;
  batchMix: number | null = null;
  selectedBatchId: number | null;
  initialBc: number | null = null;
  bc26: number | null = null;
  bc26Display = '';
  bc26Total: number | null = null;
  bc70: number | null = null;
  bc70Display = '';
  bc70Total: number | null = null;
  bc100: number | null = null;
  bc100Display = '';
  bc100Total: number | null = null;
  timeToCt: number | null = null;
  terminationTime: number | null = null;
  startedBy = '';
  startTimeLocal = '';
  startDateOnly = '';
  stoppedBy = '';
  endTimeLocal = '';
  stopDateOnly = '';
  equipmentNumber = '';
  comments = '';
  rheologyTemperature: number | null = null;
  rheologySpringFactor: number | null = 1;
  rheologyK1: number | null = null;
  rheologyK2: number | null = null;
  rheology10SecGelStrength: number | null = null;
  rheology10MinGelStrength: number | null = null;
  rheologyStartedBy = '';
  rheologyStoppedBy = '';
  rheologyStartDate = '';
  rheologyEquipmentNumber = '';
  rheologyComments = '';
  rheologyReadings: RheologyOutputReadingDto[] = [this.createEmptyRheologyReading()];
  hphtStartedBy = '';
  hphtStoppedBy = '';
  hphtStartDate = '';
  hphtStopDate = '';
  hphtEquipmentNumber = '';
  hphtComments = '';
  hphtReadings: HpHtRheologyOutputReadingDto[] = [];
  fluidLossFiltrateVol05Min: number | null = null;
  fluidLossFiltrateVol1Min: number | null = null;
  fluidLossFiltrateVol5Min: number | null = null;
  fluidLossFiltrateVol10Min: number | null = null;
  fluidLossFiltrateVol15Min: number | null = null;
  fluidLossFiltrateVol20Min: number | null = null;
  fluidLossFiltrateVol30Min: number | null = null;
  fluidLossTotalFiltrateVol: number | null = null;
  fluidLossTotalTestTime: number | null = null;
  fluidLossCalculated: number | null = null;
  fluidLossTemp: number | null = null;
  fluidLossStartedBy = '';
  fluidLossStartDate = '';
  fluidLossStoppedBy = '';
  fluidLossEquipmentNumber = '';
  fluidLossComments = '';
  freeFluidTemp: number | null = null;
  freeFluidMlsff: number | null = null;
  freeFluidPercentageFf: number | null = null;
  freeFluidAngle: number | null = null;
  freeFluidStartedBy = '';
  freeFluidStartTime = '';
  freeFluidStartDate = '';
  freeFluidStoppedBy = '';
  freeFluidComments = '';
  staticGelTemp: number | null = null;
  staticGelPressure: number | null = null;
  staticGelTimeto100lbs: number | null = null;
  staticGelTimeto500lbs: number | null = null;
  staticGelTimetoCsgs: number | null = null;
  staticGelJpt: number | null = null;
  staticGelTransitionTime: string | null = null;
  staticGelCsgsPeriod: number | null = null;
  staticGelTotalTimeTo100lbs: number | null = null;
  staticGelTotalTimeTo500lbs: number | null = null;
  staticGelTotalTimeToCsgs: number | null = null;
  staticGelStartedBy = '';
  staticGelStartTime = '';
  staticGelStartDate = '';
  staticGelStoppedBy = '';
  staticGelEndTime = '';
  staticGelStopDate = '';
  staticGelEquipmentNumber = '';
  staticGelComments = '';
  compressiveTemperature: number | null = null;
  compressivePressure: number | null = null;
  compressivePsi100 = '';
  compressivePsi500 = '';
  compressiveCondTime: number | null = null;
  compressiveCondTemp: number | null = null;
  compressiveTestTemp: number | null = null;
  compressiveHr8Psi: number | null = null;
  compressiveHr12Psi: number | null = null;
  compressiveDay1Psi: number | null = null;
  compressiveDay2Psi: number | null = null;
  compressiveEndPsi: number | null = null;
  compressiveEndTime: number | null = null;
  compressiveLengthOfTest: number | null = null;
  compressiveStartedBy = '';
  compressiveStartTime = '';
  compressiveStartDate = '';
  compressiveStoppedBy = '';
  compressiveStopTime = '';
  compressiveStopDate = '';
  compressiveEquipmentNumber = '';
  compressiveComments = '';
  compressiveReadings: CompressiveStrengthOutputReadingDto[] = [this.createEmptyCompressiveReading()];
  foamStabilityTemp: number | null = null;
  foamStabilityDensity: number | null = null;
  foamStabilityStable: string | null = null;
  foamStabilityStartedBy = '';
  foamStabilityStartTime = '';
  foamStabilityStoppedBy = '';
  foamStabilityComments = '';
  acidSolubilityTemp: number | null = null;
  acidSolubilityInitialMass: number | null = null;
  acidSolubility1Hr: number | null = null;
  acidSolubility4Hr: number | null = null;
  acidSolubility8Hr: number | null = null;
  acidSolubility12Hr: number | null = null;
  acidSolubility1Day: number | null = null;
  acidSolubility2Day: number | null = null;
  acidSolubility3Day: number | null = null;
  acidSolubility4Day: number | null = null;
  acidSolubility7Day: number | null = null;
  acidSolubility1HrPercent: number | null = null;
  acidSolubility4HrPercent: number | null = null;
  acidSolubility8HrPercent: number | null = null;
  acidSolubility12HrPercent: number | null = null;
  acidSolubility1DayPercent: number | null = null;
  acidSolubility2DayPercent: number | null = null;
  acidSolubility3DayPercent: number | null = null;
  acidSolubility4DayPercent: number | null = null;
  acidSolubility7DayPercent: number | null = null;
  acidSolubilityStartedBy = '';
  acidSolubilityStoppedBy = '';
  acidSolubilityComments = '';
  basicCrushLoadRate: number | null = null;
  basicCrushCsValue: number | null = null;
  basicCrushStartedBy = '';
  basicCrushStoppedBy = '';
  basicCrushComments = '';
  apiSedimentationTemp: number | null = null;
  apiSedimentationPressure: number | null = null;
  apiSedimentationCuringTime: number | null = null;
  apiSedimentationPiece1: number | null = null;
  apiSedimentationPiece2: number | null = null;
  apiSedimentationPiece3: number | null = null;
  apiSedimentationPiece4: number | null = null;
  apiSedimentationStartedBy = '';
  apiSedimentationStartTime = '';
  apiSedimentationStartDate = '';
  apiSedimentationStoppedBy = '';
  apiSedimentationStopTime = '';
  apiSedimentationStopDate = '';
  apiSedimentationEquipmentNumber = '';
  apiSedimentationComments = '';
  expansionTemp: number | null = null;
  expansionPressure: number | null = null;
  expansionCuringTime: number | null = null;
  expansionPercentExpansion: number | null = null;
  expansionPercentShrinkage: number | null = null;
  expansionStartedBy = '';
  expansionStartTime = '';
  expansionStartDate = '';
  expansionStoppedBy = '';
  expansionStopTime = '';
  expansionStopDate = '';
  expansionEquipmentNumber = '';
  expansionComments = '';
  pressurizedMudScaleDensity: number | null = null;
  pressurizedMudScaleStartedBy = '';
  pressurizedMudScaleStartDate = '';
  pressurizedMudScaleComments = '';
  pycnometerSampleId: number | null = null;
  pycnometerSpecificGravity: number | null = null;
  pycnometerAverageDensity: number | null = null;
  pycnometerRVolume: number | null = null;
  pycnometerCVolume: number | null = null;
  pycnometerStartedBy = '';
  pycnometerStoppedBy = '';
  pycnometerComments = '';
  shearBondTemp: number | null = null;
  shearBondPressure: number | null = null;
  shearBondCuringTime: number | null = null;
  shearBondStrength: number | null = null;
  shearBondStartedBy = '';
  shearBondStartTime = '';
  shearBondStartDate = '';
  shearBondStoppedBy = '';
  shearBondStopTime = '';
  shearBondStopDate = '';
  shearBondEquipmentNumber = '';
  shearBondComments = '';
  splittingTsValue1: number | null = null;
  splittingTsValue2: number | null = null;
  splittingTsValue3: number | null = null;
  splittingTsValue4: number | null = null;
  splittingTsValue5: number | null = null;
  splittingTsValue6: number | null = null;
  splittingTsStartedBy = '';
  splittingTsStoppedBy = '';
  splittingTsComments = '';
  triaxialConfinedCsReadings: TriaxialConfinedCsOutputReadingDto[] = this.createDefaultTriaxialConfinedCsReadings();
  triaxialConfinedCsStartedBy = '';
  triaxialConfinedCsStoppedBy = '';
  triaxialConfinedCsComments = '';
  unconfinedCsReadings: UnconfinedCsOutputReadingDto[] = this.createDefaultUnconfinedCsReadings();
  unconfinedCsStartedBy = '';
  unconfinedCsStoppedBy = '';
  unconfinedCsComments = '';
  rawFileImportMessage = '';
  outputActionError = '';
  isOutputActionInProgress = false;
  private loadedOutputStatus: number | null = null;
  readonly hphtRpmLabels = ['600', '300', '200', '100', '60', '30', '20', '10', '6', '3'];
  readonly hphtRpmFields: Array<keyof HpHtRheologyDirectionalReadingDto> = ['measuredRpm600', 'measuredRpm300', 'measuredRpm200', 'measuredRpm100', 'measuredRpm60', 'measuredRpm30', 'measuredRpm20', 'measuredRpm10', 'measuredRpm6', 'measuredRpm3'];

  get isThickeningKind(): boolean {
    return (this.data.kind ?? 'thickening') === 'thickening';
  }

  get isRheologyKind(): boolean {
    return this.data.kind === 'rheology';
  }

  get isSurfaceRheology(): boolean {
    return this.data.rheologyIsSurface !== false;
  }

  get showRheologyKValues(): boolean {
    return !this.isSurfaceRheology;
  }

  get rheologySectionTitle(): string {
    return this.isSurfaceRheology ? 'Rheology Test - Surface' : 'Rheology Test - Downhole';
  }

  get rheologyMeasured10SecGelStrength(): number | null {
    return this.calculateMeasuredRpm(this.rheology10SecGelStrength, this.rheologySpringFactor);
  }

  get rheologyMeasured10MinGelStrength(): number | null {
    return this.calculateMeasuredRpm(this.rheology10MinGelStrength, this.rheologySpringFactor);
  }

  onRheologyRawReadingChanged(
    reading: RheologyOutputReadingDto,
    field: keyof Pick<RheologyOutputReadingDto,
      'rawRpm600' | 'rawRpm300' | 'rawRpm200' | 'rawRpm100' | 'rawRpm60' |
      'rawRpm30' | 'rawRpm20' | 'rawRpm10' | 'rawRpm6' | 'rawRpm3'>,
    value: number | string | null
  ): void {
    const parsed = value === '' || value == null ? null : Number(value);
    reading[field] = parsed != null && Number.isFinite(parsed) ? parsed : null;
    this.calculateRheologyReadings();
  }

  onRheologySpringFactorChanged(value: number | string | null): void {
    const parsed = value === '' || value == null ? null : Number(value);
    this.rheologySpringFactor = parsed != null && Number.isFinite(parsed) ? parsed : null;
    this.calculateRheologyReadings();
  }

  private calculateRheologyReadings(): void {
    const factor = this.rheologySpringFactor;
    for (const reading of this.rheologyReadings) {
      reading.measuredRpm600 = this.calculateMeasuredRpm(reading.rawRpm600, factor);
      reading.measuredRpm300 = this.calculateMeasuredRpm(reading.rawRpm300, factor);
      reading.measuredRpm200 = this.calculateMeasuredRpm(reading.rawRpm200, factor);
      reading.measuredRpm100 = this.calculateMeasuredRpm(reading.rawRpm100, factor);
      reading.measuredRpm60 = this.calculateMeasuredRpm(reading.rawRpm60, factor);
      reading.measuredRpm30 = this.calculateMeasuredRpm(reading.rawRpm30, factor);
      reading.measuredRpm20 = this.calculateMeasuredRpm(reading.rawRpm20, factor);
      reading.measuredRpm10 = this.calculateMeasuredRpm(reading.rawRpm10, factor);
      reading.measuredRpm6 = this.calculateMeasuredRpm(reading.rawRpm6, factor);
      reading.measuredRpm3 = this.calculateMeasuredRpm(reading.rawRpm3, factor);
    }
  }

  private calculateMeasuredRpm(rawRpm: number | null | undefined, springFactor: number | null): number | null {
    if (rawRpm == null || springFactor == null || springFactor <= 0) {
      return null;
    }

    const measuredRpm = rawRpm * springFactor;
    return Number.isFinite(measuredRpm) ? Math.round(measuredRpm) : null;
  }

  get isHpHtRheologyKind(): boolean {
    return this.data.kind === 'hphtRheology';
  }

  get isFluidLossKind(): boolean {
    return this.data.kind === 'fluidLoss';
  }

  get fluidLossSectionTitle(): string {
    const title = (this.data.title ?? '').toLowerCase();
    return title.includes('stirred') ? 'Stirred Fluid Loss Test' : 'Fluid Loss Test';
  }

  get isFreeFluidKind(): boolean {
    return this.data.kind === 'freeFluid';
  }

  get isStaticGelKind(): boolean {
    return this.isStaticGelSgsaKind || this.isStaticGelMacsKind;
  }

  get isStaticGelSgsaKind(): boolean {
    return this.data.kind === 'staticGelSgsa';
  }

  get isStaticGelMacsKind(): boolean {
    return this.data.kind === 'staticGelMacs';
  }

  get isCompressiveStrengthKind(): boolean {
    return this.isCompressiveStrengthUcaKind || this.isCompressiveStrengthCrushKind;
  }

  get isCompressiveStrengthUcaKind(): boolean {
    return this.data.kind === 'compressiveStrengthUca';
  }

  get isCompressiveStrengthCrushKind(): boolean {
    return this.data.kind === 'compressiveStrengthCrush';
  }

  get isFoamStabilityKind(): boolean {
    return this.data.kind === 'foamStability';
  }

  get isAcidSolubilityKind(): boolean {
    return this.data.kind === 'acidSolubility';
  }

  get isBasicCrushKind(): boolean {
    return this.data.kind === 'basicCrush';
  }

  get isApiSedimentationKind(): boolean {
    return this.data.kind === 'apiSedimentation';
  }

  get isExpansionKind(): boolean {
    return this.data.kind === 'expansion';
  }

  get isPressurizedMudScaleKind(): boolean {
    return this.data.kind === 'pressurizedMudScale';
  }

  get isPycnometerKind(): boolean {
    return this.data.kind === 'pycnometer';
  }

  get isShearBondKind(): boolean {
    return this.data.kind === 'shearBond';
  }

  get isSplittingTsKind(): boolean {
    return this.data.kind === 'splittingTs';
  }

  get isTriaxialConfinedCsKind(): boolean {
    return this.data.kind === 'triaxialConfinedCs';
  }

  get isUnconfinedCsKind(): boolean {
    return this.data.kind === 'unconfinedCs';
  }

  get triaxialConfinedCsGroupedReadings(): Array<{ groupIndex: number; label: string; readings: TriaxialConfinedCsOutputReadingDto[] }> {
    return this.groupTriaxialReadings(this.triaxialConfinedCsReadings);
  }

  get unconfinedCsGroupedReadings(): Array<{ groupIndex: number; label: string; readings: UnconfinedCsOutputReadingDto[] }> {
    return this.groupUnconfinedReadings(this.unconfinedCsReadings);
  }

  get dialogTitle(): string {
    const title = this.outputTitle;
    return this.data.mode === 'review'
      ? `Test Output - ${title}`
      : `Record Test Output - ${title}`;
  }

  private get outputTitle(): string {
    const title = this.data.title?.trim();
    if (title) {
      return title;
    }
    return this.isThickeningKind ? 'Thickening Time' : 'Test Output';
  }

  get selectedBatchLabel(): string {
    const batch = this.data.batches.find(x => x.slurryBatchId === this.selectedBatchId);
    return batch?.batchNumber || (batch?.slurryBatchId != null ? `${batch.slurryBatchId}` : '--');
  }

  get outputStatusLabel(): string {
    return this.legacyOutputStatusLabel(this.currentOutputStatus, this.currentOutputStatusName);
  }

  private legacyOutputStatusLabel(status: number | null | undefined, statusName?: string | null): string {
    const dbStatusName = statusName?.trim();
    const statusNameMap: Record<string, string> = {
      started: 'InProgress',
      inprogress: 'InProgress',
      accepted: 'Approved',
      approved: 'Approved',
      rejected: 'Rejected',
      needretake: 'Repeat',
      failed: 'Failed',
      finished: 'Completed',
      completed: 'Completed',
      submitted: 'Completed',
      cancelled: 'Cancelled'
    };

    if (dbStatusName) {
      return statusNameMap[dbStatusName.toLowerCase().replace(/\s+/g, '')] ?? dbStatusName;
    }

    if (status == null) {
      return '--';
    }

    const map: Record<number, string> = {
      1: 'InProgress',
      2: 'Approved',
      3: 'Rejected',
      4: 'Repeat',
      5: 'Failed',
      6: 'Completed',
      7: 'Cancelled'
    };

    return map[status] ?? `${status}`;
  }

  private get currentOutputStatusName(): string | null {
    return this.data.output?.statusName?.trim() || null;
  }

  private get currentOutputStatusKey(): string | null {
    return this.currentOutputStatusName?.toLowerCase().replace(/\s+/g, '') ?? null;
  }

  private get isLegacyInProgressStatus(): boolean {
    return (this.currentOutputStatusKey === 'inprogress' || this.currentOutputStatusKey === 'started') ||
      (this.currentOutputStatusKey == null && this.currentOutputStatus === 1);
  }

  private get isLegacyCompletedStatus(): boolean {
    return (this.currentOutputStatusKey === 'completed' || this.currentOutputStatusKey === 'finished') ||
      (this.currentOutputStatusKey == null && this.currentOutputStatus === 6);
  }

  private get isLegacyApprovedStatus(): boolean {
    return (this.currentOutputStatusKey === 'approved' || this.currentOutputStatusKey === 'accepted') ||
      (this.currentOutputStatusKey == null && this.currentOutputStatus === 2);
  }

  private get isLegacyRejectedStatus(): boolean {
    return this.currentOutputStatusKey === 'rejected' || (this.currentOutputStatusKey == null && this.currentOutputStatus === 3);
  }

  private get isLegacyNeedRetakeStatus(): boolean {
    return this.currentOutputStatusKey === 'needretake'
      || this.currentOutputStatusKey === 'repeat'
      || (this.currentOutputStatusKey == null && this.currentOutputStatus === 4);
  }

  private get isLegacyFailedStatus(): boolean {
    return this.currentOutputStatusKey === 'failed' || (this.currentOutputStatusKey == null && this.currentOutputStatus === 5);
  }

  get genericOutputSummaryMessage(): string {
    const title = this.outputTitle;
    if (this.data.mode === 'review') {
      return `Review summary for ${title}. Output details stay scoped to the selected batch and test output.`;
    }

    return `Record output for ${title} against the selected active batch.`;
  }

  get isBatchesAvailable(): boolean {
    return this.data.mode === 'review' || this.activeBatches.length > 0 || this.outputBatch != null;
  }

  get isSubmittedMode(): boolean {
    return this.data.mode === 'review' || (this.data.output?.status ?? null) === 2;
  }

  get selectedBatch(): SlurryBatchDto | null {
    return this.data.batches.find(x => x.slurryBatchId === this.selectedBatchId) ?? null;
  }

  get outputBatch(): SlurryBatchDto | null {
    return this.data.output?.slurryBatchId == null
      ? null
      : this.data.batches.find(x => x.slurryBatchId === this.data.output?.slurryBatchId) ?? null;
  }

  get selectedBatchTest(): { slurryBatchId: number; testInputMasterId: number; testOutputMasterId?: number; status: number } | null {
    return this.selectedBatch?.tests.find(x => x.testInputMasterId === this.data.testInputMasterId) ?? null;
  }

  get currentOutputStatus(): number | null {
    const batchStatus = this.selectedBatchTest?.status ?? null;
    const loadedStatus = this.loadedOutputStatus;
    const outputStatus = this.data.output?.status ?? null;

    if (this.data.mode === 'review') {
      return outputStatus ?? loadedStatus ?? batchStatus;
    }

    return batchStatus != null && batchStatus !== 1 ? batchStatus : loadedStatus ?? outputStatus ?? batchStatus;
  }

  get isFieldReadonly(): boolean {
    return !this.canEditTestOutputInfo;
  }

  get isCalculatedReadonly(): boolean {
    return true;
  }

  get isSubmittedLikeStatus(): boolean {
    return this.currentOutputStatus != null && this.currentOutputStatus !== 1;
  }

  get workflowModeLabel(): string {
    if (this.data.mode === 'record' && this.showUpdateAction) {
      return 'Update';
    }
    if (this.showReviewActions || this.showRepeatAction) {
      return 'Review';
    }
    if (this.showUpdateAction) {
      return 'Update';
    }
    if (this.showRecordActions) {
      return 'Record';
    }
    return 'Read Only';
  }

  get showBatchSelector(): boolean {
    return this.availableBatches.length > 0;
  }

  get showBatchAsReadonlyLabel(): boolean {
    return this.data.mode === 'review' || this.isSubmittedLikeStatus || !this.canSelectBatch;
  }

  get canSelectBatch(): boolean {
    return this.data.mode !== 'review' && !this.isSubmittedLikeStatus && !this.needsReview && this.availableBatches.length > 1;
  }

  get needsReview(): boolean {
    return this.workflowActions.canVerify;
  }

  get canUpdateRecord(): boolean {
    return this.workflowActions.canUpdate;
  }

  get canRetake(): boolean {
    return this.workflowActions.canRetake;
  }

  get showRecordActions(): boolean {
    return this.workflowActions.canRecord;
  }

  get showUpdateAction(): boolean {
    return this.canUpdateRecord;
  }

  get showReviewActions(): boolean {
    return this.needsReview;
  }

  get showApproveAction(): boolean {
    return this.needsReview;
  }

  get showRejectAction(): boolean {
    return this.needsReview;
  }

  get showRepeatAction(): boolean {
    return this.canRetake;
  }

  get showRevertAction(): boolean {
    return this.workflowActions.canRevert;
  }

  get showCloseOnlyAction(): boolean {
    return this.data.mode === 'review' && !this.showReviewActions && !this.showRepeatAction && !this.showRevertAction && !this.showUpdateAction;
  }

  get isRecordActionDisabled(): boolean {
    return !this.hasLegacyTestOutput || !this.isLegacyOutputValid || this.isRheologySpringFactorRequired;
  }

  get isFailOrCancelActionDisabled(): boolean {
    return this.selectedBatch == null || !this.hasLegacyTestOutput;
  }

  get isUpdateActionDisabled(): boolean {
    return !this.hasLegacyTestOutput || !this.isLegacyOutputValid || this.isRheologySpringFactorRequired;
  }

  get isRheologySpringFactorRequired(): boolean {
    return this.isRheologyKind &&
      this.rheologyReadings.some(reading => this.hasUncalculatedRawRheologyReading(reading)) &&
      !(this.rheologySpringFactor != null && this.rheologySpringFactor > 0);
  }

  get isRevertActionDisabled(): boolean {
    return false;
  }

  private get hasLegacyTestOutput(): boolean {
    return this.canSaveCurrentKind && this.selectedBatchTest != null;
  }

  private get isLegacyOutputValid(): boolean {
    return true;
  }

  private canPersistOutputStatus(status: number | null | undefined): boolean {
    return status === 1 ||
      status === 5 ||
      (this.currentOutputStatus != null && status === this.currentOutputStatus && (this.isLegacyCompletedStatus || this.isLegacyFailedStatus));
  }

  private resolvePersistableOutput(): { slurryBatchId: number; testInputMasterId: number; testOutputMasterId?: number; status: number } | null {
    const dialogOutput = this.data.output;
    if (
      dialogOutput?.testOutputMasterId &&
      dialogOutput.testInputMasterId === this.data.testInputMasterId &&
      this.canPersistOutputStatus(dialogOutput.status)
    ) {
      return dialogOutput;
    }

    const batch = this.availableBatches.find(x => x.slurryBatchId === this.selectedBatchId);
    return batch?.tests.find(x =>
      x.testInputMasterId === this.data.testInputMasterId &&
      this.canPersistOutputStatus(x.status)) ?? null;
  }

  get workflowHintMessage(): string {
    if (this.data.mode === 'review') {
      if (this.needsReview) {
        return this.canRetake
          ? 'Review this submitted output and choose Approve, Reject, or Repeat.'
          : 'Review this submitted output and choose Approve or Reject.';
      }
      if (this.canRetake) {
        return 'This accepted output can be marked for repeat.';
      }
      if (this.showRevertAction) {
        return 'This output already has a decision recorded. Revert sends it back to the legacy completed/submitted state and reopens the input.';
      }
      return 'This review view is read-only for the selected batch and test output.';
    }

    if (this.showUpdateAction) {
      return 'This output is already completed or failed. You can update the saved record.';
    }

    return 'Select an active batch to record this test output.';
  }

  private get workflowActions(): TestOutputWorkflowActions {
    return { ...this.defaultWorkflowActions, ...(this.data.workflowActions ?? {}) };
  }

  private get securityContextAllowsWorkflow(): boolean {
    return this.data.isParentProjectCompleted !== true &&
      (this.data.slurryStatusId == null || this.data.slurryStatusId === 3);
  }

  private get canEditTestOutputInfo(): boolean {
    return this.securityContextAllowsWorkflow && (this.isLegacyInProgressStatus || this.isLegacyCompletedStatus || this.isLegacyFailedStatus);
  }

  private get defaultWorkflowActions(): TestOutputWorkflowActions {
    // Mirrors legacy CommonTestOutputViewModel + SecurityControllerFactory:
    // Record/Fail/Cancel: InProgress; Update/Edit: Completed or Failed;
    // Verify: Completed; Retake: Completed or Approved; Revert: Approved/NeedRetake/Rejected.
    const isInProgress = this.isLegacyInProgressStatus;
    const isCompleted = this.isLegacyCompletedStatus;
    const isApproved = this.isLegacyApprovedStatus;
    const isRejected = this.isLegacyRejectedStatus;
    const isNeedRetake = this.isLegacyNeedRetakeStatus;
    const isFailed = this.isLegacyFailedStatus;

    return {
      canRecord: this.securityContextAllowsWorkflow && isInProgress,
      canFail: this.securityContextAllowsWorkflow && isInProgress,
      canCancel: this.securityContextAllowsWorkflow && isInProgress,
      canRevert: this.securityContextAllowsWorkflow && (isApproved || isRejected || isNeedRetake),
      canUpdate: this.securityContextAllowsWorkflow && (isCompleted || isFailed),
      canVerify: this.securityContextAllowsWorkflow && isCompleted,
      canRetake: this.securityContextAllowsWorkflow && (isCompleted || isApproved)
    };
  }

  get staticGelCsgsLabel(): string {
    return 'Time to CSGS';
  }

  get crushIsCylinder(): boolean {
    return (this.data.crushTestTypeId ?? 1) === 2;
  }

  get compressiveAveragePressureLabel(): string {
    const included = this.compressiveReadings.filter(reading => reading.isInclude !== false && reading.psi != null);
    if (!included.length) {
      return '--';
    }

    const average = sumFiniteNumbers(included.map(reading => reading.psi)) / included.length;
    return `${Math.round(average * 100) / 100}`;
  }

  get compressiveLengthOfTestLabel(): string {
    return this.compressiveLengthOfTest != null ? `${this.compressiveLengthOfTest} hr (psi)` : 'PSI';
  }

  get canSaveCurrentKind(): boolean {
    return this.availableBatches.length > 0 && (
      this.isThickeningKind ||
      this.isRheologyKind ||
      this.isHpHtRheologyKind ||
      this.isFluidLossKind ||
      this.isFreeFluidKind ||
      this.isStaticGelKind ||
      this.isCompressiveStrengthKind ||
      this.isFoamStabilityKind ||
      this.isAcidSolubilityKind ||
      this.isBasicCrushKind ||
      this.isApiSedimentationKind ||
      this.isExpansionKind ||
      this.isPressurizedMudScaleKind ||
      this.isPycnometerKind ||
      this.isShearBondKind ||
      this.isSplittingTsKind ||
      this.isTriaxialConfinedCsKind ||
      this.isUnconfinedCsKind
    );
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: ThickeningOutputDialogData,
    private readonly dialogRef: MatDialogRef<ThickeningOutputDialogComponent>,
    private readonly slurryService: SlurryService
  ) {
    this.selectedBatchId = data.output?.slurryBatchId ?? this.activeBatches[0]?.slurryBatchId ?? null;
    if (data.mode === 'record') {
      this.initializeRecordOutputFromSchedule();
    }
    if (this.isThickeningKind && data.mode === 'review' && data.output) {
      this.slurryService.getThickeningOutput(data.slurryId, data.output.testOutputMasterId).subscribe({
        next: output => {
          this.ctTemp = output.ctTemp ?? null;
          this.thickeningPressure = output.pressure ?? null;
          this.batchMix = output.batchMix ?? null;
          this.initialBc = output.initialBc ?? null;
          this.bc26 = output.bc26 ?? null;
          this.bc26Display = this.minutesToLegacyTime(this.bc26);
          this.bc26Total = output.bc26Total ?? null;
          this.bc70 = output.bc70 ?? null;
          this.bc70Display = this.minutesToLegacyTime(this.bc70);
          this.bc70Total = output.bc70Total ?? null;
          this.bc100 = output.bc100 ?? null;
          this.bc100Display = this.minutesToLegacyTime(this.bc100);
          this.bc100Total = output.bc100Total ?? null;
          this.timeToCt = output.timeToCt ?? null;
          this.terminationTime = output.terminationTime ?? null;
          this.startedBy = output.startedBy ?? '';
          this.startTimeLocal = this.toTimeValue(output.startTime);
          this.startDateOnly = this.toDateOnlyValue(output.startDate);
          this.stoppedBy = output.stoppedBy ?? '';
          this.endTimeLocal = this.toTimeValue(output.endTime);
          this.stopDateOnly = this.toDateOnlyValue(output.stopDate);
          this.equipmentNumber = output.equipmentNumber ?? '';
          this.comments = output.comments ?? '';
        }
      });
    }

    if (this.isRheologyKind && data.mode === 'review' && data.output) {
      this.loadRheologyOutput(data.output.testOutputMasterId);
    }

    if (this.isHpHtRheologyKind) {
      if (data.mode === 'review' && data.output) {
        this.loadHpHtRheologyOutput(data.output.testOutputMasterId);
      } else {
        this.initializeHpHtReadingsFromBatch();
      }
    }

    if (this.isFluidLossKind && data.mode === 'review' && data.output) {
      this.loadFluidLossOutput(data.output.testOutputMasterId);
    }

    if (this.isFreeFluidKind && data.mode === 'review' && data.output) {
      this.loadFreeFluidOutput(data.output.testOutputMasterId);
    }

    if (this.isStaticGelKind && data.mode === 'review' && data.output) {
      this.loadStaticGelOutput(data.output.testOutputMasterId);
    }

    if (this.isCompressiveStrengthKind && data.mode === 'review' && data.output) {
      this.loadCompressiveStrengthOutput(data.output.testOutputMasterId);
    }

    if (this.isFoamStabilityKind && data.mode === 'review' && data.output) {
      this.loadFoamStabilityOutput(data.output.testOutputMasterId);
    }

    if (this.isAcidSolubilityKind && data.mode === 'review' && data.output) {
      this.loadAcidSolubilityOutput(data.output.testOutputMasterId);
    }

    if (this.isBasicCrushKind && data.mode === 'review' && data.output) {
      this.loadBasicCrushOutput(data.output.testOutputMasterId);
    }

    if (this.isApiSedimentationKind && data.mode === 'review' && data.output) {
      this.loadApiSedimentationOutput(data.output.testOutputMasterId);
    }

    if (this.isExpansionKind && data.mode === 'review' && data.output) {
      this.loadExpansionOutput(data.output.testOutputMasterId);
    }

    if (this.isPressurizedMudScaleKind && data.mode === 'review' && data.output) {
      this.loadPressurizedMudScaleOutput(data.output.testOutputMasterId);
    }

    if (this.isPycnometerKind && data.mode === 'review' && data.output) {
      this.loadPycnometerOutput(data.output.testOutputMasterId);
    }

    if (this.isShearBondKind && data.mode === 'review' && data.output) {
      this.loadShearBondOutput(data.output.testOutputMasterId);
    }

    if (this.isSplittingTsKind && data.mode === 'review' && data.output) {
      this.loadSplittingTsOutput(data.output.testOutputMasterId);
    }

    if (this.isTriaxialConfinedCsKind) {
      if (data.mode === 'review' && data.output) {
        this.loadTriaxialConfinedCsOutput(data.output.testOutputMasterId);
      } else {
        this.ensureTriaxialConfinedCsReadings();
      }
    }

    if (this.isUnconfinedCsKind) {
      if (data.mode === 'review' && data.output) {
        this.loadUnconfinedCsOutput(data.output.testOutputMasterId);
      } else {
        this.ensureUnconfinedCsReadings();
      }
    }

  }

  private initializeRecordOutputFromSchedule(): void {
    const schedule = this.data.scheduleDefaults;
    if (!schedule) {
      return;
    }

    const temperature = this.data.testInputTemperature ?? schedule.finalTemperature;
    const pressure = this.data.testInputPressure ?? schedule.finalPressure;

    if (this.isThickeningKind) {
      this.ctTemp = schedule.finalTemperature;
      this.thickeningPressure = schedule.finalPressure;
      this.timeToCt = schedule.temperatureRampTime;
    } else if (this.isRheologyKind) {
      this.rheologyTemperature = temperature;
    } else if (this.isFluidLossKind) {
      this.fluidLossTemp = temperature;
    } else if (this.isFreeFluidKind) {
      this.freeFluidTemp = temperature;
    } else if (this.isStaticGelKind) {
      this.staticGelTemp = temperature;
      this.staticGelPressure = pressure;
    } else if (this.isCompressiveStrengthKind) {
      this.compressiveTemperature = temperature;
      this.compressivePressure = pressure;
    } else if (this.isFoamStabilityKind) {
      this.foamStabilityTemp = temperature;
    } else if (this.isAcidSolubilityKind) {
      this.acidSolubilityTemp = temperature;
    } else if (this.isApiSedimentationKind) {
      this.apiSedimentationTemp = temperature;
      this.apiSedimentationPressure = pressure;
    } else if (this.isExpansionKind) {
      this.expansionTemp = temperature;
      this.expansionPressure = pressure;
    } else if (this.isShearBondKind) {
      this.shearBondTemp = temperature;
      this.shearBondPressure = pressure;
    }
  }

  get activeBatches(): SlurryBatchDto[] {
    if (this.data.testInputMasterId == null) {
      return [];
    }

    return this.data.batches.filter(batch => batch.tests.some(test =>
      test.testInputMasterId === this.data.testInputMasterId && test.status === 1));
  }

  get availableBatches(): SlurryBatchDto[] {
    if (this.data.output && this.outputBatch) {
      return [this.outputBatch];
    }

    return this.activeBatches;
  }

  get isFluidLossRawUploadDisabled(): boolean {
    return this.isFieldReadonly;
  }

  onBc70DisplayChanged(value: string): void {
    this.applyLegacyThickeningBcEdit('bc70', value);
  }

  onBc26DisplayChanged(value: string): void {
    this.applyLegacyThickeningBcEdit('bc26', value);
  }

  onBc100DisplayChanged(value: string): void {
    this.applyLegacyThickeningBcEdit('bc100', value);
  }

  onBatchMixChanged(value: number | null): void {
    this.batchMix = value;
    const batchMix = value ?? 0;
    const bc26 = this.calculateLegacyThickeningBc(this.bc26, this.bc26Total, batchMix);
    const bc70 = this.calculateLegacyThickeningBc(this.bc70, this.bc70Total, batchMix);
    const bc100 = this.calculateLegacyThickeningBc(this.bc100, this.bc100Total, batchMix);
    this.bc26 = bc26.value;
    this.bc26Total = bc26.total;
    this.bc26Display = this.minutesToLegacyTime(this.bc26);
    this.bc70 = bc70.value;
    this.bc70Total = bc70.total;
    this.bc70Display = this.minutesToLegacyTime(this.bc70);
    this.bc100 = bc100.value;
    this.bc100Total = bc100.total;
    this.bc100Display = this.minutesToLegacyTime(this.bc100);
  }

  normalizeCompressiveTime(field: 'psi100' | 'psi500', value: string): void {
    const minutes = this.legacyTimeToMinutes(value);
    if (minutes == null) {
      return;
    }

    const normalized = this.minutesToLegacyTime(minutes);
    if (field === 'psi100') {
      this.compressivePsi100 = normalized;
    } else {
      this.compressivePsi500 = normalized;
    }
  }

  get bc26TotalDisplay(): string {
    return this.minutesToLegacyTime(this.bc26Total);
  }

  get bc70TotalDisplay(): string {
    return this.minutesToLegacyTime(this.bc70Total);
  }

  get bc100TotalDisplay(): string {
    return this.minutesToLegacyTime(this.bc100Total);
  }

  close(): void {
    this.dialogRef.close();
  }

  async onRawFileSelected(event: Event, kind: 'thickening' | 'fluidLoss' | 'uca'): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) {
      return;
    }

    try {
      this.rawFileImportMessage = '';
      const expectedExtension = kind === 'fluidLoss' ? '.txt' : '.csv';
      if (!file.name.toLowerCase().endsWith(expectedExtension)) {
        this.rawFileImportMessage = `Select a ${expectedExtension} file.`;
        return;
      }

      const text = await file.text();
      let imported = false;
      if (kind !== 'fluidLoss') {
        const rows = this.parseLegacyRawCsvRows(text);
        const dataMarkerIndex = this.findLegacyRawDataMarker(rows);
        if (dataMarkerIndex >= 0 && kind === 'thickening') {
          imported = this.applyLegacyThickeningCsv(rows, dataMarkerIndex);
        } else if (dataMarkerIndex >= 0 && kind === 'uca') {
          imported = this.applyLegacyUcaCsv(rows, dataMarkerIndex);
        }
      }

      if (!imported) {
        const values = this.parseRawFileValues(text);
        if (kind === 'thickening') {
          imported = this.applyThickeningRawFile(values);
        } else if (kind === 'fluidLoss') {
          imported = this.applyFluidLossRawFile(values);
        } else {
          imported = this.applyUcaRawFile(values);
        }
      }
      if (imported) {
        this.rawFileImportMessage = `${file.name} imported.`;
      } else if (!this.rawFileImportMessage) {
        this.rawFileImportMessage = 'No recognizable test fields were found in the selected file.';
      }
    } catch (error) {
      this.rawFileImportMessage = error instanceof Error ? error.message : 'Import Error';
    }
  }

  private applyLegacyThickeningCsv(rows: string[][], dataMarkerIndex: number): boolean {
    const readings = this.parseLegacyRawCsvReadings(rows, dataMarkerIndex, 3);
    const initialReading = readings.find(reading => reading.elapsedTime === '0:00:00');
    const initialBc = initialReading?.value ?? 0;
    const bc70Reading = readings
      .filter(reading => reading.value >= 70)
      .sort((left, right) => left.value - right.value)[0];
    const bc70Total = this.parseLegacyElapsedTimeMinutes(bc70Reading?.elapsedTime ?? null);
    const batchMix = this.batchMix ?? 0;
    const hasBatchMix = batchMix > 0;
    const bc70 = bc70Total == null
      ? null
      : bc70Total > 0 && hasBatchMix
        ? bc70Total - batchMix
        : bc70Total;

    this.equipmentNumber = this.getLegacyRawCsvValue(rows, 2) ?? '';
    this.startDateOnly = this.parseLegacyRawCsvDate(this.getLegacyRawCsvValue(rows, 5));
    this.startTimeLocal = this.parseLegacyRawCsvTime(this.getLegacyRawCsvValue(rows, 6));
    this.stopDateOnly = this.parseLegacyRawCsvDate(this.getLegacyRawCsvValue(rows, 7));
    this.endTimeLocal = this.parseLegacyRawCsvTime(this.getLegacyRawCsvValue(rows, 8));
    this.initialBc = readings.length > 0 && initialBc !== 0 ? initialBc : null;
    this.bc70 = bc70;
    this.bc70Display = this.minutesToLegacyTime(this.bc70);
    this.bc70Total = hasBatchMix && (bc70Total ?? 0) <= 0 ? null : bc70Total;
    this.bc26 = null;
    this.bc26Display = '';
    this.bc26Total = null;
    this.bc100 = null;
    this.bc100Display = '';
    this.bc100Total = null;
    return true;
  }

  private applyLegacyThickeningBcEdit(field: 'bc26' | 'bc70' | 'bc100', value: string): void {
    const total = this.legacyTimeToMinutes(value);
    const batchMix = this.batchMix ?? 0;
    const hasBatchMix = batchMix > 0;
    const adjusted = total == null
      ? null
      : total > 0 && hasBatchMix
        ? total - batchMix
        : total;
    const normalizedTotal = hasBatchMix && (total ?? 0) <= 0 ? null : total;

    if (field === 'bc26') {
      this.bc26 = adjusted;
      this.bc26Total = normalizedTotal;
      this.bc26Display = this.minutesToLegacyTime(adjusted);
    } else if (field === 'bc70') {
      this.bc70 = adjusted;
      this.bc70Total = normalizedTotal;
      this.bc70Display = this.minutesToLegacyTime(adjusted);
    } else {
      this.bc100 = adjusted;
      this.bc100Total = normalizedTotal;
      this.bc100Display = this.minutesToLegacyTime(adjusted);
    }
  }

  private calculateLegacyThickeningBc(
    value: number | null,
    total: number | null,
    batchMix: number
  ): { value: number | null; total: number | null } {
    const currentValue = value ?? 0;
    const currentTotal = total ?? 0;
    if (batchMix > 0) {
      return currentValue > 0
        ? { value: currentTotal - batchMix, total }
        : { value, total: null };
    }

    return currentTotal > 0
      ? { value: currentTotal, total }
      : { value, total: value };
  }

  private applyLegacyUcaCsv(rows: string[][], dataMarkerIndex: number): boolean {
    const readings = this.parseLegacyRawCsvReadings(rows, dataMarkerIndex, 4);
    const timeTo100Psi = this.findLegacyUcaThresholdTime(readings, 100);
    const timeTo500Psi = this.findLegacyUcaThresholdTime(readings, 500);
    const hasReadings = readings.length > 0;
    const pressure12Hr = hasReadings ? readings.find(reading => reading.elapsedTime.startsWith('12'))?.value ?? 0 : null;
    const pressure24Hr = hasReadings ? readings.find(reading => reading.elapsedTime.startsWith('24'))?.value ?? 0 : null;
    const pressure48Hr = hasReadings ? readings.find(reading => reading.elapsedTime.startsWith('48'))?.value ?? 0 : null;
    const endPressure = hasReadings ? [...readings].reverse().find(reading => reading.value > 0)?.value ?? 0 : null;

    this.compressiveEquipmentNumber = this.getLegacyRawCsvValue(rows, 2) ?? '';
    this.compressiveStartDate = this.parseLegacyRawCsvDate(this.getLegacyRawCsvValue(rows, 5));
    this.compressiveStartTime = this.parseLegacyRawCsvTime(this.getLegacyRawCsvValue(rows, 6));
    this.compressiveStopDate = this.parseLegacyRawCsvDate(this.getLegacyRawCsvValue(rows, 7));
    this.compressiveStopTime = this.parseLegacyRawCsvTime(this.getLegacyRawCsvValue(rows, 8));
    this.compressivePsi100 = this.minutesToLegacyTime(timeTo100Psi);
    this.compressivePsi500 = this.minutesToLegacyTime(timeTo500Psi);
    this.compressiveHr12Psi = pressure12Hr;
    this.compressiveDay1Psi = pressure24Hr;
    this.compressiveDay2Psi = pressure48Hr;
    this.compressiveEndPsi = endPressure;
    this.compressiveEndTime = this.parseLegacyRawTotalHours(this.getLegacyRawCsvValue(rows, 10));
    return true;
  }

  private parseLegacyRawCsvRows(text: string): string[][] {
    return text.split(/\r?\n/).map(line => this.splitRawDelimitedLine(line, ','));
  }

  private findLegacyRawDataMarker(rows: string[][]): number {
    return rows.findIndex(row => row[0]?.replace(/^\uFEFF/, '').toLowerCase() === '--data--');
  }

  private getLegacyRawCsvValue(rows: string[][], rowIndex: number): string | null {
    const value = rows[rowIndex]?.[1]?.trim();
    return value || null;
  }

  private parseLegacyRawCsvReadings(
    rows: string[][],
    dataMarkerIndex: number,
    valueColumnIndex: number
  ): Array<{ elapsedTime: string; value: number }> {
    return rows.slice(dataMarkerIndex + 2)
      .filter(row => Boolean(row[0]?.trim()) && row.length > valueColumnIndex)
      .map(row => ({
        elapsedTime: row[0].trim(),
        value: this.parseLegacyRawInteger(row[valueColumnIndex])
      }));
  }

  private parseLegacyRawInteger(value?: string): number {
    return /^[-+]?\d+$/.test(value?.trim() ?? '') ? Number(value) : 0;
  }

  private parseLegacyElapsedTimeMinutes(value: string | null): number | null {
    const match = /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(value?.trim() ?? '');
    if (!match) {
      return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3]);
    if (hours > 23 || minutes > 59 || seconds > 59) {
      return null;
    }

    return hours * 60 + minutes;
  }

  private findLegacyUcaThresholdTime(
    readings: Array<{ elapsedTime: string; value: number }>,
    threshold: number
  ): number | null {
    const reading = readings.find(candidate => candidate.value >= threshold);
    return this.parseLegacyElapsedTimeMinutes(reading?.elapsedTime ?? null);
  }

  private parseLegacyRawTotalHours(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parts = value.split(':');
    if (parts.length <= 2) {
      return null;
    }

    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
      return null;
    }

    return minutes > 30 ? hours + 1 : hours;
  }

  private parseLegacyRawCsvDate(value: string | null): string {
    if (!value) {
      return '';
    }

    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (match) {
      const month = Number(match[1]);
      const day = Number(match[2]);
      const year = Number(match[3]);
      const date = new Date(year, month - 1, day);
      if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }

    return this.toDateOnlyValue(value);
  }

  private parseLegacyRawCsvTime(value: string | null): string {
    if (!value) {
      return '';
    }

    const match = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i.exec(value);
    if (!match) {
      return '';
    }

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridiem = match[3]?.toUpperCase();
    if (minutes > 59 || (meridiem ? hours < 1 || hours > 12 : hours > 23)) {
      return '';
    }
    if (meridiem) {
      hours = (hours % 12) + (meridiem === 'PM' ? 12 : 0);
    }

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private applyThickeningRawFile(values: Map<string, string>): boolean {
    const equipmentNumber = this.pickRawString(values, ['equipmentno', 'equipmentnumber', 'equipment']);
    const startDate = this.pickRawDate(values, ['startdate', 'starteddate']);
    const startTime = this.pickRawTime(values, ['starttime', 'startedtime']);
    const stopDate = this.pickRawDate(values, ['enddate', 'stopdate', 'stoppeddate']);
    const stopTime = this.pickRawTime(values, ['endtime', 'stoptime', 'stoppedtime']);
    const initialBc = this.pickRawNumber(values, ['initialbc', 'initialbeardenconsistency']);
    const bc70 = this.pickRawNumber(values, ['bc70', '70bc']);

    if (![equipmentNumber, startDate, startTime, stopDate, stopTime, initialBc, bc70].some(value => value != null)) {
      return false;
    }

    this.equipmentNumber = equipmentNumber ?? this.equipmentNumber;
    this.startDateOnly = startDate ?? this.startDateOnly;
    this.startTimeLocal = startTime ?? this.startTimeLocal;
    this.stopDateOnly = stopDate ?? this.stopDateOnly;
    this.endTimeLocal = stopTime ?? this.endTimeLocal;
    this.initialBc = initialBc ?? this.initialBc;
    this.bc70 = bc70 ?? this.bc70;
    this.bc70Display = this.minutesToLegacyTime(this.bc70);
    this.bc26 = null;
    this.bc26Display = '';
    this.bc100 = null;
    this.bc100Display = '';
    return true;
  }

  private applyFluidLossRawFile(values: Map<string, string>): boolean {
    const vol5Min = this.pickRawNumber(values, ['vol5min', 'volume5min', 'filtratevol5min', 'filtratevolume5min', '5minute', '5min']);
    const vol15Min = this.pickRawNumber(values, ['vol15min', 'volume15min', 'filtratevol15min', 'filtratevolume15min', '15minute', '15min']);
    const vol30Min = this.pickRawNumber(values, ['vol30min', 'volume30min', 'filtratevol30min', 'filtratevolume30min', '30minute', '30min']);

    if (vol5Min == null && vol15Min == null && vol30Min == null) {
      this.rawFileImportMessage = 'Mass values for 5 Minute, 15 Minute and 30 Minute not found in selected file.';
      return false;
    }

    this.fluidLossFiltrateVol5Min = vol5Min ?? this.fluidLossFiltrateVol5Min;
    this.fluidLossFiltrateVol15Min = vol15Min ?? this.fluidLossFiltrateVol15Min;
    this.fluidLossFiltrateVol30Min = vol30Min ?? this.fluidLossFiltrateVol30Min;
    return true;
  }

  private applyUcaRawFile(values: Map<string, string>): boolean {
    const equipmentNumber = this.pickRawString(values, ['equipmentno', 'equipmentnumber', 'equipment']);
    const startDate = this.pickRawDate(values, ['startdate', 'starteddate']);
    const startTime = this.pickRawTime(values, ['starttime', 'startedtime']);
    const stopDate = this.pickRawDate(values, ['enddate', 'stopdate', 'stoppeddate']);
    const stopTime = this.pickRawTime(values, ['endtime', 'stoptime', 'stoppedtime']);
    const psi100 = this.pickRawDurationAsLegacyTime(values, ['timeto100psi', 'psi100', '100psi']);
    const psi500 = this.pickRawDurationAsLegacyTime(values, ['timeto500psi', 'psi500', '500psi']);
    const hr12Psi = this.pickRawNumber(values, ['pressure12hr', 'hr12psi', '12hrpsi', '12hr']);
    const day1Psi = this.pickRawNumber(values, ['pressure24hr', 'day1psi', '24hrpsi', '24hr']);
    const day2Psi = this.pickRawNumber(values, ['pressure48hr', 'day2psi', '48hrpsi', '48hr']);
    const endPsi = this.pickRawNumber(values, ['endpressure', 'endpsi']);
    const endTime = this.pickRawNumber(values, ['totalhours', 'totaltime', 'endtimehrs', 'endtimehours']);
    const importedValues = [
      equipmentNumber, startDate, startTime, stopDate, stopTime,
      psi100, psi500, hr12Psi, day1Psi, day2Psi, endPsi, endTime
    ];

    if (!importedValues.some(value => value != null)) {
      return false;
    }

    this.compressiveEquipmentNumber = equipmentNumber ?? this.compressiveEquipmentNumber;
    this.compressiveStartDate = startDate ?? this.compressiveStartDate;
    this.compressiveStartTime = startTime ?? this.compressiveStartTime;
    this.compressiveStopDate = stopDate ?? this.compressiveStopDate;
    this.compressiveStopTime = stopTime ?? this.compressiveStopTime;
    this.compressivePsi100 = psi100 ?? this.compressivePsi100;
    this.compressivePsi500 = psi500 ?? this.compressivePsi500;
    this.compressiveHr12Psi = hr12Psi ?? this.compressiveHr12Psi;
    this.compressiveDay1Psi = day1Psi ?? this.compressiveDay1Psi;
    this.compressiveDay2Psi = day2Psi ?? this.compressiveDay2Psi;
    this.compressiveEndPsi = endPsi ?? this.compressiveEndPsi;
    this.compressiveEndTime = endTime ?? this.compressiveEndTime;
    return true;
  }

  private parseRawFileValues(text: string): Map<string, string> {
    const values = new Map<string, string>();
    const lines = text.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    const delimiter = this.detectRawFileDelimiter(lines[0] ?? '');
    const rows = delimiter ? lines.map(line => this.splitRawDelimitedLine(line, delimiter)) : [];
    const headerIndex = rows.findIndex(row =>
      row.filter(value => this.isRecognizedRawFileField(value)).length >= 2);

    if (headerIndex >= 0) {
      const headers = rows[headerIndex].map(value => this.normalizeRawKey(value));
      for (const row of rows.slice(headerIndex + 1)) {
        headers.forEach((header, index) => {
          const value = row[index]?.trim();
          if (RAW_FILE_FIELD_KEYS.has(header) && value && !values.has(header)) {
            values.set(header, value);
          }
        });
      }
      return values;
    }

    for (const line of lines) {
      const cells = delimiter ? this.splitRawDelimitedLine(line, delimiter) : [];
      if (delimiter && cells.length > 1 && this.isRecognizedRawFileField(cells[0])) {
        const key = this.normalizeRawKey(cells[0]);
        const value = cells.slice(1).join(delimiter).trim();
        if (value && !values.has(key)) {
          values.set(key, value);
        }
        continue;
      }

      const trimmed = line.trim();
      const separatorIndex = [':', '='].map(separator => trimmed.indexOf(separator))
        .filter(index => index >= 0)
        .sort((left, right) => left - right)[0];
      if (separatorIndex == null) {
        continue;
      }

      const key = this.normalizeRawKey(trimmed.slice(0, separatorIndex));
      const value = trimmed.slice(separatorIndex + 1).trim();
      if (this.isRecognizedRawFileField(key) && value && !values.has(key)) {
        values.set(key, value);
      }
    }

    return values;
  }

  private detectRawFileDelimiter(line: string): string | null {
    const delimiters = [',', '\t', ';'];
    const counts = delimiters.map(delimiter => ({
      delimiter,
      count: this.splitRawDelimitedLine(line, delimiter).length - 1
    }));
    const best = counts.reduce((current, candidate) =>
      candidate.count > current.count ? candidate : current, { delimiter: '', count: 0 });
    return best.count > 0 ? best.delimiter : null;
  }

  private splitRawDelimitedLine(line: string, delimiter: string): string[] {
    const cells: string[] = [];
    let cell = '';
    let inQuotes = false;

    for (let index = 0; index < line.length; index++) {
      const char = line[index];
      if (char === '"') {
        if (inQuotes && line[index + 1] === '"') {
          cell += '"';
          index++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        cells.push(cell.trim());
        cell = '';
      } else {
        cell += char;
      }
    }

    cells.push(cell.trim());
    return cells;
  }

  private isRecognizedRawFileField(key: string): boolean {
    return RAW_FILE_FIELD_KEYS.has(this.normalizeRawKey(key));
  }

  private pickRawString(values: Map<string, string>, keys: string[]): string | null {
    for (const key of keys) {
      const value = values.get(this.normalizeRawKey(key));
      if (value) {
        return value;
      }
    }
    return null;
  }

  private pickRawNumber(values: Map<string, string>, keys: string[]): number | null {
    const value = this.pickRawString(values, keys);
    if (!value) {
      return null;
    }

    const match = /-?\d+(?:\.\d+)?/.exec(value.replace(/,/g, ''));
    if (!match) {
      return null;
    }

    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private pickRawDurationAsLegacyTime(values: Map<string, string>, keys: string[]): string | null {
    const value = this.pickRawString(values, keys);
    if (!value) {
      return null;
    }

    if (/^-?\d{1,4}:\d{1,2}$/.test(value.trim())) {
      const minutes = this.legacyTimeToMinutes(value);
      return minutes == null ? null : this.minutesToLegacyTime(minutes);
    }

    const match = /-?\d+/.exec(value.replace(/,/g, ''));
    if (!match) {
      return null;
    }

    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? this.minutesToLegacyTime(parsed) : null;
  }

  private minutesToLegacyTime(totalMinutes: number | null | undefined): string {
    if (totalMinutes == null || !Number.isFinite(totalMinutes)) {
      return '';
    }

    const sign = totalMinutes < 0 ? '-' : '';
    const absoluteMinutes = Math.abs(Math.trunc(totalMinutes));
    const hours = Math.trunc(absoluteMinutes / 60);
    const minutes = absoluteMinutes - hours * 60;
    return `${sign}${hours}:${String(minutes).padStart(2, '0')}`;
  }

  private legacyTimeToMinutes(value: string | null | undefined): number | null {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
      return null;
    }

    const parts = trimmed.split(':');
    const integerPattern = /^[+-]?\d+$/;
    if (parts.length === 2) {
      const hours = parts[0].trim();
      const minutes = parts[1].trim();
      if (!integerPattern.test(hours) || !integerPattern.test(minutes)) {
        return null;
      }

      return Number(hours) * 60 + Number(minutes);
    }

    return integerPattern.test(trimmed) ? Number(trimmed) * 60 : null;
  }

  private pickRawDate(values: Map<string, string>, keys: string[]): string | null {
    const value = this.pickRawString(values, keys);
    if (!value) {
      return null;
    }
    return this.toDateOnlyValue(value) || null;
  }

  private pickRawTime(values: Map<string, string>, keys: string[]): string | null {
    const value = this.pickRawString(values, keys);
    if (!value) {
      return null;
    }
    return this.toTimeValue(value) || (/^\d{1,2}:\d{2}/.test(value) ? value.slice(0, 5).padStart(5, '0') : null);
  }

  private normalizeRawKey(key: string): string {
    return key.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  addRheologyReading(): void {
    this.rheologyReadings = [...this.rheologyReadings, this.createEmptyRheologyReading()];
  }

  addCompressiveReading(): void {
    this.compressiveReadings = [...this.compressiveReadings, this.createEmptyCompressiveReading()];
  }

  save(): void {
    this.persistOutputForStatus(this.selectedBatchTest?.status ?? 1);
  }

  recordOutput(): void {
    this.persistOutputForStatus(6);
  }

  updateOutput(): void {
    this.persistOutputForStatus(this.selectedBatchTest?.status ?? this.currentOutputStatus ?? 3);
  }

  failOutput(): void {
    this.persistOutputForStatus(5);
  }

  cancelOutput(): void {
    this.persistOutputForStatus(7);
  }

  approveOutput(): void {
    this.executeWorkflowStatusChange(2, 'approve');
  }

  rejectOutput(): void {
    this.executeWorkflowStatusChange(3, 'reject');
  }

  repeatOutput(): void {
    this.executeWorkflowStatusChange(4, 'repeat');
  }

  revertOutput(): void {
    if (this.isOutputActionInProgress) {
      return;
    }

    const testOutputMasterId = this.resolveWorkflowTestOutputId();
    if (testOutputMasterId == null) {
      return;
    }

    this.isOutputActionInProgress = true;
    this.outputActionError = '';
    this.slurryService.revertTestOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: result => {
        this.isOutputActionInProgress = false;
        this.dialogRef.close({ refresh: true, action: result.action.toLowerCase(), status: result.status });
      },
      error: error => {
        this.isOutputActionInProgress = false;
        this.outputActionError = this.getOutputActionErrorMessage(error);
      }
    });
  }

  private executeWorkflowStatusChange(status: number, action: 'approve' | 'reject' | 'repeat'): void {
    if (this.isOutputActionInProgress) {
      return;
    }

    const testOutputMasterId = this.resolveWorkflowTestOutputId();
    if (testOutputMasterId == null) {
      return;
    }

    this.isOutputActionInProgress = true;
    this.outputActionError = '';
    this.slurryService.changeTestOutputStatus(this.data.slurryId, testOutputMasterId, status).subscribe({
      next: result => {
        this.isOutputActionInProgress = false;
        this.dialogRef.close({ refresh: true, action, status: result.status });
      },
      error: error => {
        this.isOutputActionInProgress = false;
        this.outputActionError = this.getOutputActionErrorMessage(error);
      }
    });
  }

  private resolveWorkflowTestOutputId(): number | null {
    return this.data.output?.testOutputMasterId ?? this.selectedBatchTest?.testOutputMasterId ?? null;
  }

  private saveOutputRequest<T>(request: Observable<T>, status: number): void {
    if (this.isOutputActionInProgress) {
      return;
    }

    this.isOutputActionInProgress = true;
    this.outputActionError = '';
    request.subscribe({
      next: () => {
        this.isOutputActionInProgress = false;
        this.dialogRef.close({ refresh: true, status });
      },
      error: error => {
        this.isOutputActionInProgress = false;
        this.outputActionError = this.getOutputActionErrorMessage(error);
      }
    });
  }

  private getOutputActionErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error;
      if (typeof body === 'object' && body !== null) {
        if ('detail' in body && typeof body.detail === 'string' && body.detail.trim()) {
          return body.detail;
        }
        if ('title' in body && typeof body.title === 'string' && body.title.trim()) {
          return body.title;
        }
      }
      return error.message || 'Unable to save test output. Please try again.';
    }

    return error instanceof Error && error.message
      ? error.message
      : 'Unable to save test output. Please try again.';
  }

  private persistOutputForStatus(status: number): void {
    if (this.isOutputActionInProgress) {
      return;
    }

    if (this.isRheologyKind) {
      this.saveRheologyOutput(status);
      return;
    }

    if (this.isHpHtRheologyKind) {
      this.saveHpHtRheologyOutput(status);
      return;
    }

    if (this.isFluidLossKind) {
      this.saveFluidLossOutput(status);
      return;
    }

    if (this.isFreeFluidKind) {
      this.saveFreeFluidOutput(status);
      return;
    }

    if (this.isStaticGelKind) {
      this.saveStaticGelOutput(status);
      return;
    }

    if (this.isCompressiveStrengthKind) {
      this.saveCompressiveStrengthOutput(status);
      return;
    }

    if (this.isFoamStabilityKind) {
      this.saveFoamStabilityOutput(status);
      return;
    }

    if (this.isAcidSolubilityKind) {
      this.saveAcidSolubilityOutput(status);
      return;
    }

    if (this.isBasicCrushKind) {
      this.saveBasicCrushOutput(status);
      return;
    }

    if (this.isApiSedimentationKind) {
      this.saveApiSedimentationOutput(status);
      return;
    }

    if (this.isExpansionKind) {
      this.saveExpansionOutput(status);
      return;
    }

    if (this.isPressurizedMudScaleKind) {
      this.savePressurizedMudScaleOutput(status);
      return;
    }

    if (this.isPycnometerKind) {
      this.savePycnometerOutput(status);
      return;
    }

    if (this.isShearBondKind) {
      this.saveShearBondOutput(status);
      return;
    }

    if (this.isSplittingTsKind) {
      this.saveSplittingTsOutput(status);
      return;
    }

    if (this.isTriaxialConfinedCsKind) {
      this.saveTriaxialConfinedCsOutput(status);
      return;
    }

    if (this.isUnconfinedCsKind) {
      this.saveUnconfinedCsOutput(status);
      return;
    }

    if (!this.isThickeningKind) {
      this.dialogRef.close(false);
      return;
    }

    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveThickeningOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      ctTemp: this.ctTemp,
      pressure: this.thickeningPressure,
      batchMix: this.batchMix,
      initialBc: this.initialBc,
      bc26: this.bc26,
      bc26Total: this.bc26Total,
      bc70: this.bc70,
      bc70Total: this.bc70Total,
      bc100: this.bc100,
      bc100Total: this.bc100Total,
      timeToCt: this.timeToCt,
      terminationTime: this.terminationTime,
      startedBy: this.emptyToNull(this.startedBy),
      startTime: this.toDateTimePayload(this.startTimeLocal, this.startDateOnly),
      startDate: this.emptyToNull(this.startDateOnly),
      stoppedBy: this.emptyToNull(this.stoppedBy),
      endTime: this.toDateTimePayload(this.endTimeLocal, this.stopDateOnly),
      stopDate: this.emptyToNull(this.stopDateOnly),
      equipmentNumber: this.emptyToNull(this.equipmentNumber),
      comments: this.emptyToNull(this.comments)
    }), status);
  }

  private toTimeValue(value?: string | null): string {
    if (!value) {
      return '';
    }

    const timeMatch = /(?:T|^)(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/.exec(value);
    if (timeMatch) {
      return timeMatch[1];
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (part: number) => part.toString().padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private toDateTimePayload(timeValue: string, dateValue?: string | null): string | null {
    const time = this.emptyToNull(timeValue);
    if (!time) {
      return null;
    }

    if (time.includes('T')) {
      return time;
    }

    const date = this.emptyToNull(dateValue ?? '') ?? this.toDateOnlyValue(new Date().toISOString());
    return `${date}T${time.length === 5 ? `${time}:00` : time}`;
  }

  private toDateOnlyValue(value?: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (part: number) => part.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private emptyToNull(value: string): string | null {
    const normalized = value?.trim();
    return normalized ? normalized : null;
  }

  private applyLegacySimpleOutput(output?: BatchTestBriefDto | null): void {
    if (!output) {
      return;
    }

    const startedBy = output.reviewedBy ?? output.updateUser ?? '';

    if (this.isApiSedimentationKind) {
      this.apiSedimentationStartedBy = startedBy;
      return;
    }

    if (this.isExpansionKind) {
      this.expansionStartedBy = startedBy;
      return;
    }

    if (this.isPressurizedMudScaleKind) {
      this.pressurizedMudScaleStartedBy = startedBy;
      return;
    }

    if (this.isPycnometerKind) {
      this.pycnometerStartedBy = startedBy;
      return;
    }

    if (this.isShearBondKind) {
      this.shearBondStartedBy = startedBy;
      return;
    }

    if (this.isSplittingTsKind) {
      this.splittingTsStartedBy = startedBy;
      return;
    }

    if (this.isTriaxialConfinedCsKind) {
      this.triaxialConfinedCsStartedBy = startedBy;
      return;
    }

    if (this.isUnconfinedCsKind) {
      this.unconfinedCsStartedBy = startedBy;
    }
  }

  addTriaxialConfinedCsGroup(): void {
    const nextGroup = this.triaxialConfinedCsGroupedReadings.length + 1;
    this.triaxialConfinedCsReadings = [...this.triaxialConfinedCsReadings, ...this.createTriaxialGroup(nextGroup)];
  }

  removeTriaxialConfinedCsGroup(groupIndex: number): void {
    this.triaxialConfinedCsReadings = this.reindexTriaxialReadings(this.triaxialConfinedCsReadings.filter(reading => (reading.groupIndex ?? 0) !== groupIndex));
    if (!this.triaxialConfinedCsReadings.length) {
      this.triaxialConfinedCsReadings = this.createDefaultTriaxialConfinedCsReadings();
    }
  }

  addUnconfinedCsGroup(): void {
    const nextGroup = this.unconfinedCsGroupedReadings.length + 1;
    this.unconfinedCsReadings = [...this.unconfinedCsReadings, ...this.createUnconfinedGroup(nextGroup)];
  }

  removeUnconfinedCsGroup(groupIndex: number): void {
    this.unconfinedCsReadings = this.reindexUnconfinedReadings(this.unconfinedCsReadings.filter(reading => (reading.groupIndex ?? 0) !== groupIndex));
    if (!this.unconfinedCsReadings.length) {
      this.unconfinedCsReadings = this.createDefaultUnconfinedCsReadings();
    }
  }

  private loadRheologyOutput(testOutputMasterId: number): void {
    this.slurryService.getRheologyOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyRheologyOutput(output)
    });
  }

  setDirectionalValue(reading: HpHtRheologyDirectionalReadingDto, field: keyof HpHtRheologyDirectionalReadingDto, value: number | string | null): void {
    const normalized = value === '' || value == null ? null : Number(value);
    reading[field] = Number.isNaN(normalized as number) ? null : normalized as number | null;
  }

  private applyRheologyOutput(output: RheologyOutputDto): void {
    this.rheologyTemperature = output.temperature ?? null;
    this.rheologySpringFactor = output.springFactor ?? 1;
    this.rheologyK1 = output.k1 ?? null;
    this.rheologyK2 = output.k2 ?? null;
    this.rheology10SecGelStrength = output.r10SecGelStrength ?? null;
    this.rheology10MinGelStrength = output.r10MinGelStrength ?? null;
    this.rheologyStartedBy = output.startedBy ?? '';
    this.rheologyStoppedBy = output.stoppedBy ?? '';
    this.rheologyStartDate = this.toDateOnlyValue(output.startDate);
    this.rheologyEquipmentNumber = output.equipmentNumber ?? '';
    this.rheologyComments = output.comments ?? '';
    this.rheologyReadings = output.readings?.length
      ? output.readings.map(reading => ({ ...reading }))
      : [this.createEmptyRheologyReading()];
  }

  private saveRheologyOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveRheologyOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      temperature: this.rheologyTemperature,
      springFactor: this.rheologySpringFactor,
      k1: this.showRheologyKValues ? this.rheologyK1 : null,
      k2: this.showRheologyKValues ? this.rheologyK2 : null,
      r10SecGelStrength: this.isSurfaceRheology ? this.rheology10SecGelStrength : null,
      r10MinGelStrength: this.isSurfaceRheology ? this.rheology10MinGelStrength : null,
      startedBy: this.emptyToNull(this.rheologyStartedBy),
      stoppedBy: this.emptyToNull(this.rheologyStoppedBy),
      startDate: this.emptyToNull(this.rheologyStartDate),
      equipmentNumber: this.emptyToNull(this.rheologyEquipmentNumber),
      comments: this.emptyToNull(this.rheologyComments),
      readings: this.rheologyReadings
        .filter(reading => this.hasRheologyReadingValue(reading))
        .map(reading => ({ ...reading }))
    }), status);
  }

  private loadHpHtRheologyOutput(testOutputMasterId: number): void {
    this.slurryService.getHpHtRheologyOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyHpHtRheologyOutput(output)
    });
  }

  private loadFluidLossOutput(testOutputMasterId: number): void {
    this.slurryService.getFluidLossOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyFluidLossOutput(output)
    });
  }

  private applyFluidLossOutput(output: FluidLossOutputDto): void {
    this.fluidLossTemp = output.temp ?? null;
    this.fluidLossFiltrateVol05Min = output.filtrateVol05Min ?? null;
    this.fluidLossFiltrateVol1Min = output.filtrateVol1Min ?? null;
    this.fluidLossFiltrateVol5Min = output.filtrateVol5Min ?? null;
    this.fluidLossFiltrateVol10Min = output.filtrateVol10Min ?? null;
    this.fluidLossFiltrateVol15Min = output.filtrateVol15Min ?? null;
    this.fluidLossFiltrateVol20Min = output.filtrateVol20Min ?? null;
    this.fluidLossFiltrateVol30Min = output.filtrateVol30Min ?? null;
    this.fluidLossTotalFiltrateVol = output.totalFiltrateVol ?? null;
    this.fluidLossTotalTestTime = output.totalTestTime ?? null;
    this.fluidLossCalculated = output.fluidLossCalculated ?? null;
    if (this.fluidLossTotalFiltrateVol != null && this.fluidLossTotalTestTime != null) {
      this.onFluidLossCalculationInputChanged();
    }
    this.fluidLossStartedBy = output.startedBy ?? '';
    this.fluidLossStartDate = this.toDateOnlyValue(output.startDate);
    this.fluidLossStoppedBy = output.stoppedBy ?? '';
    this.fluidLossEquipmentNumber = output.equipmentNumber ?? '';
    this.fluidLossComments = output.comments ?? '';
  }

  onFluidLossCalculationInputChanged(): void {
    const totalFiltrateVol = this.fluidLossTotalFiltrateVol;
    const totalTestTime = this.fluidLossTotalTestTime;
    if (totalFiltrateVol == null || totalTestTime == null ||
      !Number.isFinite(totalFiltrateVol) || !Number.isFinite(totalTestTime) ||
      totalFiltrateVol < 0 || totalTestTime <= 0) {
      this.fluidLossCalculated = null;
      return;
    }

    const calculated = totalFiltrateVol * Math.sqrt(30 / totalTestTime) * 2;
    if (!Number.isFinite(calculated) || calculated > 2147483647) {
      this.fluidLossCalculated = null;
      return;
    }

    const lower = Math.floor(calculated);
    const fraction = calculated - lower;
    this.fluidLossCalculated = fraction === 0.5
      ? (lower % 2 === 0 ? lower : lower + 1)
      : Math.round(calculated);
  }

  private loadFreeFluidOutput(testOutputMasterId: number): void {
    this.slurryService.getFreeFluidOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyFreeFluidOutput(output)
    });
  }

  private loadStaticGelOutput(testOutputMasterId: number): void {
    this.slurryService.getStaticGelOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyStaticGelOutput(output)
    });
  }

  private applyStaticGelOutput(output: StaticGelOutputDto): void {
    this.staticGelTemp = output.temperature ?? null;
    this.staticGelPressure = output.pressure ?? null;
    this.staticGelTimeto100lbs = output.timeto100lbs ?? null;
    this.staticGelTimeto500lbs = output.timeto500lbs ?? null;
    this.staticGelTimetoCsgs = output.timetoCsgs ?? null;
    this.staticGelJpt = output.jpt ?? null;
    this.staticGelTransitionTime = output.transitionTime ? output.transitionTime.substring(0, 16) : null;
    this.staticGelCsgsPeriod = output.csgsPeriod ?? null;
    this.staticGelTotalTimeTo100lbs = output.totalTimeTo100lbs ?? null;
    this.staticGelTotalTimeTo500lbs = output.totalTimeTo500lbs ?? null;
    this.staticGelTotalTimeToCsgs = output.totalTimeToCsgs ?? null;
    this.staticGelStartedBy = output.startedBy ?? '';
    this.staticGelStartTime = this.toTimeValue(output.startTime);
    this.staticGelStartDate = this.toDateOnlyValue(output.startDate);
    this.staticGelStoppedBy = output.stoppedBy ?? '';
    this.staticGelEndTime = this.toTimeValue(output.endTime);
    this.staticGelStopDate = this.toDateOnlyValue(output.stopDate);
    this.staticGelEquipmentNumber = output.equipmentNumber ?? '';
    this.staticGelComments = output.comments ?? '';
  }

  private loadCompressiveStrengthOutput(testOutputMasterId: number): void {
    this.slurryService.getCompressiveStrengthOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyCompressiveStrengthOutput(output)
    });
  }

  private applyCompressiveStrengthOutput(output: CompressiveStrengthOutputDto): void {
    this.loadedOutputStatus = output.status ?? this.loadedOutputStatus;
    this.compressiveLengthOfTest = output.lengthOfTest ?? null;
    this.compressiveTemperature = output.temperature ?? null;
    this.compressivePressure = output.pressure ?? null;
    this.compressivePsi100 = this.minutesToLegacyTime(output.psi100);
    this.compressivePsi500 = this.minutesToLegacyTime(output.psi500);
    this.compressiveCondTime = output.condTime ?? null;
    this.compressiveCondTemp = output.condTemp ?? null;
    this.compressiveTestTemp = output.testTemp ?? null;
    this.compressiveHr8Psi = output.hr8Psi ?? null;
    this.compressiveHr12Psi = output.hr12Psi ?? null;
    this.compressiveDay1Psi = output.day1Psi ?? null;
    this.compressiveDay2Psi = output.day2Psi ?? null;
    this.compressiveEndPsi = output.endPsi ?? null;
    this.compressiveEndTime = output.endTime ?? null;
    this.compressiveStartedBy = output.startedBy ?? '';
    this.compressiveStartTime = this.toTimeValue(output.startTime);
    this.compressiveStartDate = this.toDateOnlyValue(output.startDate);
    this.compressiveStoppedBy = output.stoppedBy ?? '';
    this.compressiveStopTime = this.toTimeValue(output.stopTime);
    this.compressiveStopDate = this.toDateOnlyValue(output.stopDate);
    this.compressiveEquipmentNumber = output.equipmentNumber ?? '';
    this.compressiveComments = output.comments ?? '';
    this.compressiveReadings = output.readings?.length ? output.readings.map(reading => ({ ...reading })) : [this.createEmptyCompressiveReading()];
  }

  private loadFoamStabilityOutput(testOutputMasterId: number): void {
    this.slurryService.getFoamStabilityOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyFoamStabilityOutput(output)
    });
  }

  private applyFoamStabilityOutput(output: FoamStabilityOutputDto): void {
    this.foamStabilityTemp = output.temp ?? null;
    this.foamStabilityDensity = output.density ?? null;
    this.foamStabilityStable = output.stable ?? null;
    this.foamStabilityStartedBy = output.startedBy ?? '';
    this.foamStabilityStartTime = this.toTimeValue(output.startTime);
    this.foamStabilityStoppedBy = output.stoppedBy ?? '';
    this.foamStabilityComments = output.comments ?? '';
  }

  private loadAcidSolubilityOutput(testOutputMasterId: number): void {
    this.slurryService.getAcidSolubilityOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyAcidSolubilityOutput(output)
    });
  }

  private applyAcidSolubilityOutput(output: AcidSolubilityOutputDto): void {
    this.acidSolubilityTemp = output.temp ?? null;
    this.acidSolubilityInitialMass = output.initialMass ?? null;
    this.acidSolubility1Hr = output.hr1 ?? null;
    this.acidSolubility4Hr = output.hr4 ?? null;
    this.acidSolubility8Hr = output.hr8 ?? null;
    this.acidSolubility12Hr = output.hr12 ?? null;
    this.acidSolubility1Day = output.day1 ?? null;
    this.acidSolubility2Day = output.day2 ?? null;
    this.acidSolubility3Day = output.day3 ?? null;
    this.acidSolubility4Day = output.day4 ?? null;
    this.acidSolubility7Day = output.day7 ?? null;
    this.acidSolubility1HrPercent = output.hr1Percent ?? null;
    this.acidSolubility4HrPercent = output.hr4Percent ?? null;
    this.acidSolubility8HrPercent = output.hr8Percent ?? null;
    this.acidSolubility12HrPercent = output.hr12Percent ?? null;
    this.acidSolubility1DayPercent = output.day1Percent ?? null;
    this.acidSolubility2DayPercent = output.day2Percent ?? null;
    this.acidSolubility3DayPercent = output.day3Percent ?? null;
    this.acidSolubility4DayPercent = output.day4Percent ?? null;
    this.acidSolubility7DayPercent = output.day7Percent ?? null;
    this.acidSolubilityStartedBy = output.startedBy ?? '';
    this.acidSolubilityStoppedBy = output.stoppedBy ?? '';
    this.acidSolubilityComments = output.comments ?? '';
  }

  private loadBasicCrushOutput(testOutputMasterId: number): void {
    this.slurryService.getBasicCrushOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyBasicCrushOutput(output)
    });
  }

  private applyBasicCrushOutput(output: BasicCrushOutputDto): void {
    this.basicCrushLoadRate = output.loadRate ?? null;
    this.basicCrushCsValue = output.csValue ?? null;
    this.basicCrushStartedBy = output.startedBy ?? '';
    this.basicCrushStoppedBy = output.stoppedBy ?? '';
    this.basicCrushComments = output.comments ?? '';
  }

  private loadApiSedimentationOutput(testOutputMasterId: number): void {
    this.slurryService.getApiSedimentationOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyApiSedimentationOutput(output)
    });
  }

  private applyApiSedimentationOutput(output: ApiSedimentationOutputDto): void {
    this.apiSedimentationTemp = output.temp ?? null;
    this.apiSedimentationPressure = output.pressure ?? null;
    this.apiSedimentationCuringTime = output.curingTime ?? null;
    this.apiSedimentationPiece1 = output.piece1 ?? null;
    this.apiSedimentationPiece2 = output.piece2 ?? null;
    this.apiSedimentationPiece3 = output.piece3 ?? null;
    this.apiSedimentationPiece4 = output.piece4 ?? null;
    this.apiSedimentationStartedBy = output.startedBy ?? '';
    this.apiSedimentationStartTime = this.toTimeValue(output.startTime);
    this.apiSedimentationStartDate = this.toDateOnlyValue(output.startDate);
    this.apiSedimentationStoppedBy = output.stoppedBy ?? '';
    this.apiSedimentationStopTime = this.toTimeValue(output.stopTime);
    this.apiSedimentationStopDate = this.toDateOnlyValue(output.stopDate);
    this.apiSedimentationEquipmentNumber = output.equipmentNumber ?? '';
    this.apiSedimentationComments = output.comments ?? '';
  }

  private loadExpansionOutput(testOutputMasterId: number): void {
    this.slurryService.getExpansionOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyExpansionOutput(output)
    });
  }

  private applyExpansionOutput(output: ExpansionOutputDto): void {
    this.expansionTemp = output.temp ?? null;
    this.expansionPressure = output.pressure ?? null;
    this.expansionCuringTime = output.curingTime ?? null;
    this.expansionPercentExpansion = output.percentExpansion ?? null;
    this.expansionPercentShrinkage = output.percentShrinkage ?? null;
    this.expansionStartedBy = output.startedBy ?? '';
    this.expansionStartTime = this.toTimeValue(output.startTime);
    this.expansionStartDate = this.toDateOnlyValue(output.startDate);
    this.expansionStoppedBy = output.stoppedBy ?? '';
    this.expansionStopTime = this.toTimeValue(output.stopTime);
    this.expansionStopDate = this.toDateOnlyValue(output.stopDate);
    this.expansionEquipmentNumber = output.equipmentNumber ?? '';
    this.expansionComments = output.comments ?? '';
  }

  private loadPressurizedMudScaleOutput(testOutputMasterId: number): void {
    this.slurryService.getPressurizedMudScaleOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyPressurizedMudScaleOutput(output)
    });
  }

  private applyPressurizedMudScaleOutput(output: PressurizedMudScaleOutputDto): void {
    this.pressurizedMudScaleDensity = output.density ?? null;
    this.pressurizedMudScaleStartedBy = output.startedBy ?? '';
    this.pressurizedMudScaleStartDate = this.toDateOnlyValue(output.testDate ?? output.startDate);
    this.pressurizedMudScaleComments = output.comments ?? '';
  }

  private loadPycnometerOutput(testOutputMasterId: number): void {
    this.slurryService.getPycnometerOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyPycnometerOutput(output)
    });
  }

  private applyPycnometerOutput(output: PycnometerOutputDto): void {
    this.pycnometerSampleId = output.sampleId ?? null;
    this.pycnometerSpecificGravity = output.specificGravity ?? null;
    this.pycnometerAverageDensity = output.averageDensity ?? null;
    this.pycnometerRVolume = output.rVolume ?? null;
    this.pycnometerCVolume = output.cVolume ?? null;
    this.pycnometerStartedBy = output.startedBy ?? '';
    this.pycnometerStoppedBy = output.stoppedBy ?? '';
    this.pycnometerComments = output.comments ?? '';
  }

  private loadShearBondOutput(testOutputMasterId: number): void {
    this.slurryService.getShearBondOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applyShearBondOutput(output)
    });
  }

  private applyShearBondOutput(output: ShearBondOutputDto): void {
    this.shearBondTemp = output.temp ?? null;
    this.shearBondPressure = output.pressure ?? null;
    this.shearBondCuringTime = output.curingTime ?? null;
    this.shearBondStrength = output.strength ?? null;
    this.shearBondStartedBy = output.startedBy ?? '';
    this.shearBondStartTime = this.toTimeValue(output.startTime);
    this.shearBondStartDate = this.toDateOnlyValue(output.startDate);
    this.shearBondStoppedBy = output.stoppedBy ?? '';
    this.shearBondStopTime = this.toTimeValue(output.stopTime);
    this.shearBondStopDate = this.toDateOnlyValue(output.stopDate);
    this.shearBondEquipmentNumber = output.equipmentNumber ?? '';
    this.shearBondComments = output.comments ?? '';
  }

  private loadSplittingTsOutput(testOutputMasterId: number): void {
    this.slurryService.getSplittingTsOutput(this.data.slurryId, testOutputMasterId).subscribe({
      next: output => this.applySplittingTsOutput(output)
    });
  }

  private applySplittingTsOutput(output: SplittingTsOutputDto): void {
    this.splittingTsValue1 = output.stsValue1 ?? null;
    this.splittingTsValue2 = output.stsValue2 ?? null;
    this.splittingTsValue3 = output.stsValue3 ?? null;
    this.splittingTsValue4 = output.stsValue4 ?? null;
    this.splittingTsValue5 = output.stsValue5 ?? null;
    this.splittingTsValue6 = output.stsValue6 ?? null;
    this.splittingTsStartedBy = output.startedBy ?? '';
    this.splittingTsStoppedBy = output.stoppedBy ?? '';
    this.splittingTsComments = output.comments ?? '';
  }

  private loadTriaxialConfinedCsOutput(testOutputMasterId: number): void {
    this.slurryService.getTriaxialConfinedCsOutput(this.data.slurryId, testOutputMasterId).subscribe({ next: output => this.applyTriaxialConfinedCsOutput(output) });
  }

  private applyTriaxialConfinedCsOutput(output: TriaxialConfinedCsOutputDto): void {
    this.triaxialConfinedCsReadings = output.readings?.length ? this.reindexTriaxialReadings(output.readings.map(reading => ({ ...reading }))) : this.createDefaultTriaxialConfinedCsReadings();
    this.triaxialConfinedCsStartedBy = output.startedBy ?? '';
    this.triaxialConfinedCsStoppedBy = output.stoppedBy ?? '';
    this.triaxialConfinedCsComments = output.comments ?? '';
  }

  private loadUnconfinedCsOutput(testOutputMasterId: number): void {
    this.slurryService.getUnconfinedCsOutput(this.data.slurryId, testOutputMasterId).subscribe({ next: output => this.applyUnconfinedCsOutput(output) });
  }

  private applyUnconfinedCsOutput(output: UnconfinedCsOutputDto): void {
    this.unconfinedCsReadings = output.readings?.length ? this.reindexUnconfinedReadings(output.readings.map(reading => ({ ...reading }))) : this.createDefaultUnconfinedCsReadings();
    this.unconfinedCsStartedBy = output.startedBy ?? '';
    this.unconfinedCsStoppedBy = output.stoppedBy ?? '';
    this.unconfinedCsComments = output.comments ?? '';
  }

  private applyFreeFluidOutput(output: FreeFluidOutputDto): void {
    this.freeFluidTemp = output.temp ?? null;
    this.freeFluidMlsff = output.mlsff ?? null;
    this.freeFluidPercentageFf = output.percentageFf ?? null;
    this.freeFluidAngle = output.angle ?? null;
    this.freeFluidStartedBy = output.startedBy ?? '';
    this.freeFluidStartTime = this.toTimeValue(output.startTime);
    this.freeFluidStartDate = this.toDateOnlyValue(output.startDate);
    this.freeFluidStoppedBy = output.stoppedBy ?? '';
    this.freeFluidComments = output.comments ?? '';
  }

  private applyHpHtRheologyOutput(output: HpHtRheologyOutputDto): void {
    this.hphtStartedBy = output.startedBy ?? '';
    this.hphtStoppedBy = output.stoppedBy ?? '';
    this.hphtStartDate = this.toDateOnlyValue(output.startDate);
    this.hphtStopDate = this.toDateOnlyValue(output.stopDate);
    this.hphtEquipmentNumber = output.equipmentNumber ?? '';
    this.hphtComments = output.comments ?? '';
    this.hphtReadings = output.readings?.length
      ? output.readings.map(reading => ({
          inputReadingId: reading.inputReadingId ?? null,
          temperature: reading.temperature ?? null,
          pressure: reading.pressure ?? null,
          time: reading.time ?? null,
          springFactor: reading.springFactor ?? null,
          measured10Sec: reading.measured10Sec ?? null,
          measured10Min: reading.measured10Min ?? null,
          rampUp: { ...this.createEmptyHpHtDirectionalReading(), ...(reading.rampUp ?? {}) },
          rampDown: { ...this.createEmptyHpHtDirectionalReading(), ...(reading.rampDown ?? {}) },
          average: { ...this.createEmptyHpHtDirectionalReading(), ...(reading.average ?? {}) },
          calculatedRampUp: reading.calculatedRampUp ? { ...this.createEmptyHpHtDirectionalReading(), ...reading.calculatedRampUp } : null,
          calculatedRampDown: reading.calculatedRampDown ? { ...this.createEmptyHpHtDirectionalReading(), ...reading.calculatedRampDown } : null,
          calculatedAverage: reading.calculatedAverage ? { ...this.createEmptyHpHtDirectionalReading(), ...reading.calculatedAverage } : null
        }))
      : [];
  }

  private initializeHpHtReadingsFromBatch(): void {
    const batch = this.activeBatches.find(x => x.slurryBatchId === this.selectedBatchId) ?? this.activeBatches[0];
    const output = batch?.tests.find(x => x.testInputMasterId === this.data.testInputMasterId && x.status === 1);
    if (!output?.testOutputMasterId) {
      this.hphtReadings = [];
      return;
    }

    this.loadHpHtRheologyOutput(output.testOutputMasterId);
  }

  private saveHpHtRheologyOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveHpHtRheologyOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      startedBy: this.emptyToNull(this.hphtStartedBy),
      stoppedBy: this.emptyToNull(this.hphtStoppedBy),
      startDate: this.emptyToNull(this.hphtStartDate),
      stopDate: this.emptyToNull(this.hphtStopDate),
      equipmentNumber: this.emptyToNull(this.hphtEquipmentNumber),
      comments: this.emptyToNull(this.hphtComments),
      readings: this.hphtReadings.map(reading => ({
        inputReadingId: reading.inputReadingId ?? null,
        temperature: reading.temperature ?? null,
        pressure: reading.pressure ?? null,
        time: reading.time ?? null,
        springFactor: reading.springFactor ?? null,
        measured10Sec: reading.measured10Sec ?? null,
        measured10Min: reading.measured10Min ?? null,
        rampUp: { ...reading.rampUp },
        rampDown: { ...reading.rampDown },
        average: { ...reading.average }
      }))
    }), status);
  }

  private saveFluidLossOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveFluidLossOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      temp: this.fluidLossTemp,
      filtrateVol05Min: this.fluidLossFiltrateVol05Min,
      filtrateVol1Min: this.fluidLossFiltrateVol1Min,
      filtrateVol5Min: this.fluidLossFiltrateVol5Min,
      filtrateVol10Min: this.fluidLossFiltrateVol10Min,
      filtrateVol15Min: this.fluidLossFiltrateVol15Min,
      filtrateVol20Min: this.fluidLossFiltrateVol20Min,
      filtrateVol30Min: this.fluidLossFiltrateVol30Min,
      totalFiltrateVol: this.fluidLossTotalFiltrateVol,
      totalTestTime: this.fluidLossTotalTestTime,
      fluidLossCalculated: this.fluidLossCalculated,
      startedBy: this.emptyToNull(this.fluidLossStartedBy),
      startDate: this.emptyToNull(this.fluidLossStartDate),
      stoppedBy: this.emptyToNull(this.fluidLossStoppedBy),
      equipmentNumber: this.emptyToNull(this.fluidLossEquipmentNumber),
      comments: this.emptyToNull(this.fluidLossComments)
    }), status);
  }

  private saveFreeFluidOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveFreeFluidOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      temp: this.freeFluidTemp,
      mlsff: this.freeFluidMlsff,
      percentageFf: this.freeFluidPercentageFf,
      angle: this.freeFluidAngle,
      startedBy: this.emptyToNull(this.freeFluidStartedBy),
      startTime: this.toDateTimePayload(this.freeFluidStartTime, this.freeFluidStartDate),
      startDate: this.emptyToNull(this.freeFluidStartDate),
      stoppedBy: this.emptyToNull(this.freeFluidStoppedBy),
      comments: this.emptyToNull(this.freeFluidComments)
    }), status);
  }

  private saveStaticGelOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveStaticGelOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      temperature: this.staticGelTemp,
      pressure: this.staticGelPressure,
      timeto100lbs: this.staticGelTimeto100lbs,
      timeto500lbs: this.staticGelTimeto500lbs,
      timetoCsgs: this.staticGelTimetoCsgs,
      jpt: this.staticGelJpt,
      csgsPeriod: this.staticGelCsgsPeriod,
      totalTimeTo100lbs: this.staticGelTotalTimeTo100lbs,
      totalTimeTo500lbs: this.staticGelTotalTimeTo500lbs,
      totalTimeToCsgs: this.staticGelTotalTimeToCsgs,
      transitionTime: this.staticGelTransitionTime ? new Date(this.staticGelTransitionTime).toISOString() : null,
      startedBy: this.emptyToNull(this.staticGelStartedBy),
      startTime: this.toDateTimePayload(this.staticGelStartTime, this.staticGelStartDate),
      startDate: this.emptyToNull(this.staticGelStartDate),
      stoppedBy: this.emptyToNull(this.staticGelStoppedBy),
      endTime: this.toDateTimePayload(this.staticGelEndTime, this.staticGelStopDate),
      stopDate: this.emptyToNull(this.staticGelStopDate),
      equipmentNumber: this.emptyToNull(this.staticGelEquipmentNumber),
      comments: this.emptyToNull(this.staticGelComments)
    }), status);
  }

  private saveCompressiveStrengthOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveCompressiveStrengthOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      compressiveStrengthTypeId: this.data.compressiveStrengthTypeId ?? 0,
      temperature: this.compressiveTemperature,
      pressure: this.compressivePressure,
      psi100: this.legacyTimeToMinutes(this.compressivePsi100),
      psi500: this.legacyTimeToMinutes(this.compressivePsi500),
      condTime: this.compressiveCondTime,
      condTemp: this.compressiveCondTemp,
      testTemp: this.compressiveTestTemp,
      hr8Psi: this.compressiveHr8Psi,
      hr12Psi: this.compressiveHr12Psi,
      day1Psi: this.compressiveDay1Psi,
      day2Psi: this.compressiveDay2Psi,
      endPsi: this.compressiveEndPsi,
      endTime: this.compressiveEndTime,
      startedBy: this.emptyToNull(this.compressiveStartedBy),
      startTime: this.toDateTimePayload(this.compressiveStartTime, this.compressiveStartDate),
      startDate: this.emptyToNull(this.compressiveStartDate),
      stoppedBy: this.emptyToNull(this.compressiveStoppedBy),
      stopTime: this.toDateTimePayload(this.compressiveStopTime, this.compressiveStopDate),
      stopDate: this.emptyToNull(this.compressiveStopDate),
      equipmentNumber: this.emptyToNull(this.compressiveEquipmentNumber),
      comments: this.emptyToNull(this.compressiveComments),
      readings: this.compressiveReadings.filter(reading => this.hasCompressiveReadingValue(reading)).map(reading => ({ ...reading }))
    }), status);
  }

  private saveFoamStabilityOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveFoamStabilityOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      foamedStabilityTypeId: this.data.foamedStabilityTypeId ?? 0,
      temp: this.foamStabilityTemp,
      density: this.foamStabilityDensity,
      stable: this.foamStabilityStable,
      startedBy: this.emptyToNull(this.foamStabilityStartedBy),
      startTime: this.toDateTimePayload(this.foamStabilityStartTime),
      stoppedBy: this.emptyToNull(this.foamStabilityStoppedBy),
      comments: this.emptyToNull(this.foamStabilityComments)
    }), status);
  }

  private saveAcidSolubilityOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveAcidSolubilityOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      temp: this.acidSolubilityTemp,
      initialMass: this.acidSolubilityInitialMass,
      hr1: this.acidSolubility1Hr,
      hr4: this.acidSolubility4Hr,
      hr8: this.acidSolubility8Hr,
      hr12: this.acidSolubility12Hr,
      day1: this.acidSolubility1Day,
      day2: this.acidSolubility2Day,
      day3: this.acidSolubility3Day,
      day4: this.acidSolubility4Day,
      day7: this.acidSolubility7Day,
      hr1Percent: this.acidSolubility1HrPercent,
      hr4Percent: this.acidSolubility4HrPercent,
      hr8Percent: this.acidSolubility8HrPercent,
      hr12Percent: this.acidSolubility12HrPercent,
      day1Percent: this.acidSolubility1DayPercent,
      day2Percent: this.acidSolubility2DayPercent,
      day3Percent: this.acidSolubility3DayPercent,
      day4Percent: this.acidSolubility4DayPercent,
      day7Percent: this.acidSolubility7DayPercent,
      startedBy: this.emptyToNull(this.acidSolubilityStartedBy),
      stoppedBy: this.emptyToNull(this.acidSolubilityStoppedBy),
      comments: this.emptyToNull(this.acidSolubilityComments)
    }), status);
  }

  private saveBasicCrushOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveBasicCrushOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      loadRate: this.basicCrushLoadRate,
      csValue: this.basicCrushCsValue,
      startedBy: this.emptyToNull(this.basicCrushStartedBy),
      stoppedBy: this.emptyToNull(this.basicCrushStoppedBy),
      comments: this.emptyToNull(this.basicCrushComments)
    }), status);
  }

  private saveApiSedimentationOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveApiSedimentationOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      temp: this.apiSedimentationTemp,
      pressure: this.apiSedimentationPressure,
      curingTime: this.apiSedimentationCuringTime,
      piece1: this.apiSedimentationPiece1,
      piece2: this.apiSedimentationPiece2,
      piece3: this.apiSedimentationPiece3,
      piece4: this.apiSedimentationPiece4,
      startedBy: this.emptyToNull(this.apiSedimentationStartedBy),
      startTime: this.toDateTimePayload(this.apiSedimentationStartTime, this.apiSedimentationStartDate),
      startDate: this.emptyToNull(this.apiSedimentationStartDate),
      stoppedBy: this.emptyToNull(this.apiSedimentationStoppedBy),
      stopTime: this.toDateTimePayload(this.apiSedimentationStopTime, this.apiSedimentationStopDate),
      stopDate: this.emptyToNull(this.apiSedimentationStopDate),
      equipmentNumber: this.emptyToNull(this.apiSedimentationEquipmentNumber),
      comments: this.emptyToNull(this.apiSedimentationComments)
    }), status);
  }

  private saveExpansionOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveExpansionOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      temp: this.expansionTemp,
      pressure: this.expansionPressure,
      curingTime: this.expansionCuringTime,
      percentExpansion: this.expansionPercentExpansion,
      percentShrinkage: this.expansionPercentShrinkage,
      startedBy: this.emptyToNull(this.expansionStartedBy),
      startTime: this.toDateTimePayload(this.expansionStartTime, this.expansionStartDate),
      startDate: this.emptyToNull(this.expansionStartDate),
      stoppedBy: this.emptyToNull(this.expansionStoppedBy),
      stopTime: this.toDateTimePayload(this.expansionStopTime, this.expansionStopDate),
      stopDate: this.emptyToNull(this.expansionStopDate),
      equipmentNumber: this.emptyToNull(this.expansionEquipmentNumber),
      comments: this.emptyToNull(this.expansionComments)
    }), status);
  }

  private savePressurizedMudScaleOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.savePressurizedMudScaleOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      density: this.pressurizedMudScaleDensity,
      startedBy: this.emptyToNull(this.pressurizedMudScaleStartedBy),
      startDate: this.emptyToNull(this.pressurizedMudScaleStartDate),
      testDate: this.emptyToNull(this.pressurizedMudScaleStartDate),
      comments: this.emptyToNull(this.pressurizedMudScaleComments)
    }), status);
  }

  private savePycnometerOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.savePycnometerOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      sampleId: this.pycnometerSampleId,
      specificGravity: this.pycnometerSpecificGravity,
      averageDensity: this.pycnometerAverageDensity,
      rVolume: this.pycnometerRVolume,
      cVolume: this.pycnometerCVolume,
      startedBy: this.emptyToNull(this.pycnometerStartedBy),
      stoppedBy: this.emptyToNull(this.pycnometerStoppedBy),
      comments: this.emptyToNull(this.pycnometerComments)
    }), status);
  }

  private saveShearBondOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveShearBondOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      temp: this.shearBondTemp,
      pressure: this.shearBondPressure,
      curingTime: this.shearBondCuringTime,
      strength: this.shearBondStrength,
      startedBy: this.emptyToNull(this.shearBondStartedBy),
      startTime: this.toDateTimePayload(this.shearBondStartTime, this.shearBondStartDate),
      startDate: this.emptyToNull(this.shearBondStartDate),
      stoppedBy: this.emptyToNull(this.shearBondStoppedBy),
      stopTime: this.toDateTimePayload(this.shearBondStopTime, this.shearBondStopDate),
      stopDate: this.emptyToNull(this.shearBondStopDate),
      equipmentNumber: this.emptyToNull(this.shearBondEquipmentNumber),
      comments: this.emptyToNull(this.shearBondComments)
    }), status);
  }

  private saveSplittingTsOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveSplittingTsOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      stsValue1: this.splittingTsValue1,
      stsValue2: this.splittingTsValue2,
      stsValue3: this.splittingTsValue3,
      stsValue4: this.splittingTsValue4,
      stsValue5: this.splittingTsValue5,
      stsValue6: this.splittingTsValue6,
      startedBy: this.emptyToNull(this.splittingTsStartedBy),
      stoppedBy: this.emptyToNull(this.splittingTsStoppedBy),
      comments: this.emptyToNull(this.splittingTsComments)
    }), status);
  }

  private saveTriaxialConfinedCsOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveTriaxialConfinedCsOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      startedBy: this.emptyToNull(this.triaxialConfinedCsStartedBy),
      stoppedBy: this.emptyToNull(this.triaxialConfinedCsStoppedBy),
      comments: this.emptyToNull(this.triaxialConfinedCsComments),
      readings: this.reindexTriaxialReadings(this.triaxialConfinedCsReadings.map(reading => ({ ...reading })))
    }), status);
  }

  private saveUnconfinedCsOutput(status: number): void {
    const output = this.resolvePersistableOutput();
    if (!output?.testOutputMasterId) {
      return;
    }

    this.saveOutputRequest(this.slurryService.saveUnconfinedCsOutput(this.data.slurryId, output.testOutputMasterId, {
      testOutputMasterId: output.testOutputMasterId,
      slurryBatchId: output.slurryBatchId,
      status,
      startedBy: this.emptyToNull(this.unconfinedCsStartedBy),
      stoppedBy: this.emptyToNull(this.unconfinedCsStoppedBy),
      comments: this.emptyToNull(this.unconfinedCsComments),
      readings: this.reindexUnconfinedReadings(this.unconfinedCsReadings.map(reading => ({ ...reading })))
    }), status);
  }

  private createDefaultTriaxialConfinedCsReadings(): TriaxialConfinedCsOutputReadingDto[] {
    return this.createTriaxialGroup(1);
  }

  private createTriaxialGroup(groupIndex: number): TriaxialConfinedCsOutputReadingDto[] {
    return this.confinedReadingLabels.map((_, index) => ({ groupIndex, sequence: index + 1, loadRate: null, confPress: null, temp: null, youngModulus: null, pr: null, csValue: null }));
  }

  private ensureTriaxialConfinedCsReadings(): void {
    if (!this.triaxialConfinedCsReadings.length) {
      this.triaxialConfinedCsReadings = this.createDefaultTriaxialConfinedCsReadings();
    }
  }

  private groupTriaxialReadings(readings: TriaxialConfinedCsOutputReadingDto[]): Array<{ groupIndex: number; label: string; readings: TriaxialConfinedCsOutputReadingDto[] }> {
    const groups = new Map<number, TriaxialConfinedCsOutputReadingDto[]>();
    for (const reading of this.reindexTriaxialReadings(readings.map(item => ({ ...item })))) {
      const groupIndex = reading.groupIndex ?? 1;
      const group = groups.get(groupIndex) ?? [];
      group.push(reading);
      groups.set(groupIndex, group);
    }
    return Array.from(groups.entries()).sort((a, b) => a[0] - b[0]).map(([groupIndex, groupReadings]) => ({ groupIndex, label: `Cylinder ${groupIndex}`, readings: groupReadings.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)) }));
  }

  private reindexTriaxialReadings(readings: TriaxialConfinedCsOutputReadingDto[]): TriaxialConfinedCsOutputReadingDto[] {
    return readings.sort((a, b) => (a.groupIndex ?? 0) - (b.groupIndex ?? 0) || (a.sequence ?? 0) - (b.sequence ?? 0)).map((reading, index) => ({ ...reading, groupIndex: Math.floor(index / 3) + 1, sequence: (index % 3) + 1 }));
  }

  private createDefaultUnconfinedCsReadings(): UnconfinedCsOutputReadingDto[] {
    return this.createUnconfinedGroup(1);
  }

  private createUnconfinedGroup(groupIndex: number): UnconfinedCsOutputReadingDto[] {
    return this.confinedReadingLabels.map((_, index) => ({ groupIndex, sequence: index + 1, loadRate: null, youngModulus: null, pr: null, csValue: null }));
  }

  private ensureUnconfinedCsReadings(): void {
    if (!this.unconfinedCsReadings.length) {
      this.unconfinedCsReadings = this.createDefaultUnconfinedCsReadings();
    }
  }

  private groupUnconfinedReadings(readings: UnconfinedCsOutputReadingDto[]): Array<{ groupIndex: number; label: string; readings: UnconfinedCsOutputReadingDto[] }> {
    const groups = new Map<number, UnconfinedCsOutputReadingDto[]>();
    for (const reading of this.reindexUnconfinedReadings(readings.map(item => ({ ...item })))) {
      const groupIndex = reading.groupIndex ?? 1;
      const group = groups.get(groupIndex) ?? [];
      group.push(reading);
      groups.set(groupIndex, group);
    }
    return Array.from(groups.entries()).sort((a, b) => a[0] - b[0]).map(([groupIndex, groupReadings]) => ({ groupIndex, label: `Cylinder ${groupIndex}`, readings: groupReadings.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)) }));
  }

  private reindexUnconfinedReadings(readings: UnconfinedCsOutputReadingDto[]): UnconfinedCsOutputReadingDto[] {
    return readings.sort((a, b) => (a.groupIndex ?? 0) - (b.groupIndex ?? 0) || (a.sequence ?? 0) - (b.sequence ?? 0)).map((reading, index) => ({ ...reading, groupIndex: Math.floor(index / 3) + 1, sequence: (index % 3) + 1 }));
  }

  private hasRheologyReadingValue(reading: RheologyOutputReadingDto): boolean {
    return [
      reading.rawRpm600,
      reading.rawRpm300,
      reading.rawRpm200,
      reading.rawRpm100,
      reading.rawRpm60,
      reading.rawRpm30,
      reading.rawRpm20,
      reading.rawRpm10,
      reading.rawRpm6,
      reading.rawRpm3,
      reading.measuredRpm600,
      reading.measuredRpm300,
      reading.measuredRpm200,
      reading.measuredRpm100,
      reading.measuredRpm60,
      reading.measuredRpm30,
      reading.measuredRpm20,
      reading.measuredRpm10,
      reading.measuredRpm6,
      reading.measuredRpm3
    ].some(value => value != null);
  }

  private hasUncalculatedRawRheologyReading(reading: RheologyOutputReadingDto): boolean {
    return (reading.rawRpm600 != null && reading.measuredRpm600 == null)
      || (reading.rawRpm300 != null && reading.measuredRpm300 == null)
      || (reading.rawRpm200 != null && reading.measuredRpm200 == null)
      || (reading.rawRpm100 != null && reading.measuredRpm100 == null)
      || (reading.rawRpm60 != null && reading.measuredRpm60 == null)
      || (reading.rawRpm30 != null && reading.measuredRpm30 == null)
      || (reading.rawRpm20 != null && reading.measuredRpm20 == null)
      || (reading.rawRpm10 != null && reading.measuredRpm10 == null)
      || (reading.rawRpm6 != null && reading.measuredRpm6 == null)
      || (reading.rawRpm3 != null && reading.measuredRpm3 == null);
  }

  private hasCompressiveReadingValue(reading: CompressiveStrengthOutputReadingDto): boolean {
    return [reading.cubeNo, reading.width, reading.diameter, reading.height, reading.area, reading.force, reading.psi].some(value => value != null);
  }

  private createEmptyRheologyReading(): RheologyOutputReadingDto {
    return {
      rawRpm600: null,
      rawRpm300: null,
      rawRpm200: null,
      rawRpm100: null,
      rawRpm60: null,
      rawRpm30: null,
      rawRpm20: null,
      rawRpm10: null,
      rawRpm6: null,
      rawRpm3: null,
      measuredRpm600: null,
      measuredRpm300: null,
      measuredRpm200: null,
      measuredRpm100: null,
      measuredRpm60: null,
      measuredRpm30: null,
      measuredRpm20: null,
      measuredRpm10: null,
      measuredRpm6: null,
      measuredRpm3: null
    };
  }

  private createEmptyHpHtDirectionalReading(): HpHtRheologyDirectionalReadingDto {
    return {
      measuredRpm600: null,
      measuredRpm300: null,
      measuredRpm200: null,
      measuredRpm100: null,
      measuredRpm60: null,
      measuredRpm30: null,
      measuredRpm20: null,
      measuredRpm10: null,
      measuredRpm6: null,
      measuredRpm3: null
    };
  }

  private createEmptyCompressiveReading(): CompressiveStrengthOutputReadingDto {
    return {
      cubeNo: null,
      width: null,
      diameter: null,
      height: null,
      area: null,
      force: null,
      psi: null,
      isInclude: true
    };
  }
}
